import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Toaster, toast } from 'sonner';
import {
  Play, Square, Trophy, TrendingUp, TrendingDown, Brain, Sparkles, Flame, Target,
  Loader2, AlertTriangle, FileDown, Mail, X, Activity, Clock, Award, ShieldAlert,
  Zap, ChevronDown, ChevronUp, BarChart3, Calendar, Star, MessageCircle, Send, Image as ImageIcon
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI';

const SETUP_OPTIONS = [
  { id: 'trend_following', label: 'Trend Following', icon: '📈' },
  { id: 'ma_200_rejection', label: 'MA 200 Rejection', icon: '🛡️' },
  { id: 'ma_21_rejection', label: 'MA 21 Rejection', icon: '🛡️' },
  { id: 'ma_9_rejection', label: 'MA 9 Rejection', icon: '🛡️' },
  { id: 'ma_9_21_cross', label: 'MA 9 & 21 Cross', icon: '✖️' },
  { id: 'support_rejection', label: 'Support Rejection', icon: '⬆️' },
  { id: 'resistance_rejection', label: 'Resistance Rejection', icon: '⬇️' },
  { id: 'supply_demand_retracement', label: 'Supply/Demand Retracement', icon: '🎯' },
  { id: 'ifvg', label: 'iFVG (Inverse FVG)', icon: '🔄' },
  { id: 'fvg', label: 'FVG (Fair Value Gap)', icon: '🪟' },
  { id: 'impulse_price_movement_chase', label: 'Impulse Chase', icon: '⚡' },
  { id: 'news', label: 'News Catalyst', icon: '📰' },
  { id: 'quick_scalper', label: 'Quick Scalper', icon: '🏃' },
  { id: 'ny_755_open', label: 'NY 7:55 Open', icon: '🇺🇸' },
  { id: 'ny_935_open', label: 'NY 9:35 Open', icon: '🔔' },
  { id: 'volume_confirmation', label: 'Volume Confirmation', icon: '📊' },
  { id: 'order_block', label: 'Order Block', icon: '🧱' },
];

const PHYSICAL_STATES = [
  { id: 'calm', label: 'Calm', icon: '🧘', color: 'from-emerald-500 to-emerald-600' },
  { id: 'focused', label: 'Focused', icon: '🎯', color: 'from-blue-500 to-blue-600' },
  { id: 'stressed', label: 'Stressed', icon: '😰', color: 'from-amber-500 to-amber-600' },
  { id: 'heart_racing', label: 'Heart Racing', icon: '💓', color: 'from-rose-500 to-rose-600' },
  { id: 'tired', label: 'Tired', icon: '😴', color: 'from-zinc-500 to-zinc-600' },
];

const STYLE_DETAILS = {
  scalp: { label: 'Scalp', icon: '⚡', noTradePokeMin: 30, openTradePokeMin: 15, color: 'from-rose-500 to-amber-500' },
  day: { label: 'Day Trade', icon: '🌞', noTradePokeMin: 60, openTradePokeMin: 30, color: 'from-amber-500 to-yellow-500' },
  swing: { label: 'Swing', icon: '🌊', noTradePokeMin: 120, openTradePokeMin: 60, color: 'from-yellow-500 to-emerald-500' },
};

const formatTime = (seconds) => {
  if (!seconds || seconds < 0) return '00:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s` : `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const playChime = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = 'sine'; o.frequency.value = 880;
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.05);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.8);
    o.start(); o.stop(ctx.currentTime + 0.8);
  } catch (e) { /* audio blocked - ignore */ }
};

// Voice announcement using browser TTS — used on Trade Executed / Trade Closed
const speak = (text) => {
  try {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    const synth = window.speechSynthesis;
    // Cancel any pending utterance so callbacks don't queue up
    synth.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 1.05;
    u.pitch = 1.0;
    u.volume = 1.0;
    // Try to pick a clear English voice if available
    const voices = synth.getVoices ? synth.getVoices() : [];
    const preferred = voices.find(v => /Google|Samantha|Daniel|Microsoft/i.test(v.name) && /en/i.test(v.lang))
      || voices.find(v => /en/i.test(v.lang));
    if (preferred) u.voice = preferred;
    synth.speak(u);
  } catch (e) { /* TTS not available - silent fail */ }
};

export default function Scorecard() {
  const navigate = useNavigate();
  const [token, setToken] = useState(localStorage.getItem('guestToken') || '');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  // Session state
  const [session, setSession] = useState(null);
  const [trades, setTrades] = useState([]);
  const [tradingStyle, setTradingStyle] = useState('scalp');
  const [sessionGoal, setSessionGoal] = useState('');
  const [maxLosses, setMaxLosses] = useState(3);
  const [accountType, setAccountType] = useState('eval'); // 'eval' | 'funded'
  const [fundedFirm, setFundedFirm] = useState('');
  const [fundedSize, setFundedSize] = useState('');
  const [fundedProfitTarget, setFundedProfitTarget] = useState('');
  const [fundedMaxDD, setFundedMaxDD] = useState('');
  const [now, setNow] = useState(Date.now());

  // Prop-firm Account Profile (top widget)
  const [profile, setProfile] = useState(null); // { expenses, payouts, blown_accounts }
  const [profileSummary, setProfileSummary] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showAddPayout, setShowAddPayout] = useState(false);
  const [showAddBlown, setShowAddBlown] = useState(false);
  const [profForm, setProfForm] = useState({
    amount: '', firm: '', account_size: '', type: 'eval_purchase', date: new Date().toISOString().slice(0, 10), notes: '', account_type: 'eval'
  });
  const [profSubmitting, setProfSubmitting] = useState(false);

  // Named Funded Accounts
  const [fundedAccounts, setFundedAccounts] = useState([]);
  const [selectedFundedAccountId, setSelectedFundedAccountId] = useState('');
  const [showFundedAccountModal, setShowFundedAccountModal] = useState(false);
  const [editingFundedAccount, setEditingFundedAccount] = useState(null);
  const [faForm, setFaForm] = useState({
    name: '', firm: '', account_size: '', profit_target: '', max_drawdown: '',
    starting_balance: '', current_pnl: '', status: 'active', notes: '', account_class: 'funded'
  });
  const [faSubmitting, setFaSubmitting] = useState(false);

  // Account details viewer (per-account PnL + trade history)
  const [detailAccount, setDetailAccount] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState(null);

  // Trade execute modal
  const [showExecModal, setShowExecModal] = useState(false);
  const [execSetups, setExecSetups] = useState([]);
  const [execEmotion, setExecEmotion] = useState(5);
  const [execImpulse, setExecImpulse] = useState(3);
  const [execPhysical, setExecPhysical] = useState('focused');
  const [execInstrument, setExecInstrument] = useState('');
  const [execNotes, setExecNotes] = useState('');
  const [submittingTrade, setSubmittingTrade] = useState(false);

  // Trade close modal
  const [closeModalTrade, setCloseModalTrade] = useState(null);
  const [closeOutcome, setCloseOutcome] = useState('win');
  const [closePnl, setClosePnl] = useState('');
  const [closeNotes, setCloseNotes] = useState('');
  const [closePartial, setClosePartial] = useState(false);
  const [closeReason, setCloseReason] = useState('');
  const [submittingClose, setSubmittingClose] = useState(false);

  // Per-trade AI verdict modal (shown after closing a trade)
  const [tradeVerdict, setTradeVerdict] = useState(null);

  // Cancel-session modal
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  // Manual import section
  const [manualOpen, setManualOpen] = useState(false);
  const [manualTrades, setManualTrades] = useState([]);
  const [manualDate, setManualDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [manualInstrument, setManualInstrument] = useState('');
  const [manualPnl, setManualPnl] = useState('');
  const [manualOutcome, setManualOutcome] = useState('win');
  const [manualSetups, setManualSetups] = useState([]);
  const [manualSession, setManualSession] = useState('');
  const [manualReason, setManualReason] = useState('');
  const [submittingManual, setSubmittingManual] = useState(false);

  // Smart pokes
  const [dismissedPokes, setDismissedPokes] = useState(new Set());

  // Best-setup nudge (shown after starting a new session)
  const [bestSetupNudge, setBestSetupNudge] = useState(null);

  // AI Coach Chat (live during session)
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [chatImage, setChatImage] = useState(null); // base64 data URL
  const [chatUnread, setChatUnread] = useState(0);
  const chatScrollRef = useRef(null);
  const chatFileRef = useRef(null);

  // History + stats
  const [history, setHistory] = useState([]);
  const [aggStats, setAggStats] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [emailing, setEmailing] = useState(false);

  // Session-end AI evaluation modal
  const [endModal, setEndModal] = useState(null);

  // End session confirm modal (window.confirm is blocked in iframe preview)
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [endingSession, setEndingSession] = useState(false);

  // Login form (when no token)
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);

  // ===== Auth bootstrap =====
  useEffect(() => {
    document.title = 'AI Session Coach · FORTHEWISEPRO';
    // Pre-load voices (Chrome lazy-loads them — first speak() can be silent otherwise)
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      try { window.speechSynthesis.getVoices(); } catch (e) { /* ignore */ }
    }
    if (!token) { setLoading(false); return; }
    (async () => {
      try {
        const res = await fetch(`${API}/scorecard/session/active?token=${token}`);
        const data = await res.json();
        if (res.status === 401) {
          localStorage.removeItem('guestToken');
          setToken('');
          setAuthError('Session expired. Please log in again.');
        } else if (res.status === 403) {
          setAuthError('Active subscription required to access the AI Session Coach.');
        } else if (data.success) {
          setSession(data.session);
          setTrades(data.trades || []);
          // load history & stats async
          loadHistory();
          loadAggStats();
        }
      } catch (e) {
        setAuthError('Could not connect. Please refresh.');
      } finally { setLoading(false); }
    })();
  }, [token]);

  const loadHistory = async () => {
    try {
      const res = await fetch(`${API}/scorecard/sessions?token=${token}&limit=20`);
      const data = await res.json();
      if (data.success) setHistory(data.sessions || []);
    } catch (e) { /* */ }
  };

  const loadAggStats = async () => {
    try {
      const res = await fetch(`${API}/scorecard/stats?token=${token}&days=30`);
      const data = await res.json();
      if (data.success) setAggStats(data.stats);
    } catch (e) { /* */ }
  };

  // ===== Prop-firm Account Profile =====
  const loadProfile = async () => {
    try {
      const res = await fetch(`${API}/scorecard/account/profile?token=${token}`);
      const data = await res.json();
      if (data.success) {
        setProfile(data.profile);
        setProfileSummary(data.summary);
      }
    } catch (e) { /* */ }
  };

  const submitProfileEntry = async (kind) => {
    // kind = 'expense' | 'payout' | 'blown'
    const url = `${API}/scorecard/account/${kind}?token=${token}`;
    const payload = { ...profForm };
    if (kind !== 'blown') {
      if (!payload.amount || parseFloat(payload.amount) <= 0) { toast.error('Enter a positive amount'); return; }
      payload.amount = parseFloat(payload.amount);
    } else {
      delete payload.amount;
    }
    setProfSubmitting(true);
    try {
      const res = await fetch(url, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        toast.success(
          kind === 'expense' ? '💸 Expense logged' :
          kind === 'payout' ? '💰 Payout logged' : '🪦 Account marked blown'
        );
        setShowAddExpense(false); setShowAddPayout(false); setShowAddBlown(false);
        setProfForm({ amount: '', firm: '', account_size: '', type: 'eval_purchase', date: new Date().toISOString().slice(0, 10), notes: '', account_type: 'eval' });
        loadProfile();
      } else { toast.error('Failed'); }
    } catch (e) { toast.error('Failed'); }
    finally { setProfSubmitting(false); }
  };

  const deleteProfileEntry = async (kind, id) => {
    try {
      const res = await fetch(`${API}/scorecard/account/${kind}/${id}?token=${token}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) { toast.success('Removed'); loadProfile(); }
    } catch (e) { toast.error('Delete failed'); }
  };

  useEffect(() => {
    if (token) { loadProfile(); loadFundedAccounts(); }
  }, [token]);  // eslint-disable-line

  // ===== Named Funded Accounts =====
  const loadFundedAccounts = async () => {
    try {
      const res = await fetch(`${API}/scorecard/account/funded?token=${token}`);
      const data = await res.json();
      if (data.success) setFundedAccounts(data.accounts || []);
    } catch (e) { /* */ }
  };

  const openAddFundedAccount = (klass = 'funded') => {
    setEditingFundedAccount(null);
    setFaForm({
      name: '', firm: '', account_size: '', profit_target: '', max_drawdown: '',
      starting_balance: '', current_pnl: '', status: 'active', notes: '', account_class: klass
    });
    setShowFundedAccountModal(true);
  };

  const openEditFundedAccount = (acc) => {
    setEditingFundedAccount(acc);
    setFaForm({
      name: acc.name || '',
      firm: acc.firm || '',
      account_size: acc.account_size || '',
      profit_target: acc.profit_target ?? '',
      max_drawdown: acc.max_drawdown ?? '',
      starting_balance: acc.starting_balance ?? '',
      current_pnl: acc.current_pnl ?? '',
      status: acc.status || 'active',
      notes: acc.notes || '',
      account_class: acc.account_class || 'funded'
    });
    setShowFundedAccountModal(true);
  };

  const submitFundedAccount = async () => {
    if (!faForm.name.trim()) { toast.error('Account name is required'); return; }
    setFaSubmitting(true);
    const payload = {
      name: faForm.name.trim(),
      firm: faForm.firm,
      account_size: faForm.account_size,
      profit_target: faForm.profit_target === '' ? null : parseFloat(faForm.profit_target),
      max_drawdown: faForm.max_drawdown === '' ? null : parseFloat(faForm.max_drawdown),
      starting_balance: faForm.starting_balance === '' ? null : parseFloat(faForm.starting_balance),
      current_pnl: faForm.current_pnl === '' ? 0 : parseFloat(faForm.current_pnl),
      status: faForm.status,
      account_class: faForm.account_class || 'funded',
      notes: faForm.notes
    };
    try {
      const url = editingFundedAccount
        ? `${API}/scorecard/account/funded/${editingFundedAccount.id}?token=${token}`
        : `${API}/scorecard/account/funded?token=${token}`;
      const res = await fetch(url, {
        method: editingFundedAccount ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        toast.success(editingFundedAccount ? 'Account updated' : '💼 Account saved');
        setShowFundedAccountModal(false);
        setEditingFundedAccount(null);
        loadFundedAccounts();
      } else { toast.error('Failed'); }
    } catch (e) { toast.error('Failed'); }
    finally { setFaSubmitting(false); }
  };

  const deleteFundedAccount = async (id) => {
    try {
      const res = await fetch(`${API}/scorecard/account/funded/${id}?token=${token}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) { toast.success('Account removed'); loadFundedAccounts(); if (selectedFundedAccountId === id) setSelectedFundedAccountId(''); }
    } catch (e) { toast.error('Delete failed'); }
  };

  const promoteEvalToFunded = async (id) => {
    try {
      const res = await fetch(`${API}/scorecard/account/funded/${id}/promote?token=${token}`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        toast.success('🎉 Eval passed → new Funded account created!', { duration: 5000 });
        loadFundedAccounts();
      } else { toast.error(data.detail || 'Promote failed'); }
    } catch (e) { toast.error('Promote failed'); }
  };

  const openAccountDetails = async (acc) => {
    setDetailAccount(acc);
    setDetailLoading(true);
    setDetailData(null);
    try {
      const res = await fetch(`${API}/scorecard/account/funded/${acc.id}/details?token=${token}`);
      const data = await res.json();
      if (data.success) setDetailData(data);
      else toast.error('Could not load account details');
    } catch (e) { toast.error('Load failed'); }
    finally { setDetailLoading(false); }
  };

  // When account is picked in the start-session card, auto-fill firm/size/target/DD
  const onPickFundedAccount = (id) => {
    setSelectedFundedAccountId(id);
    const acc = fundedAccounts.find(a => a.id === id);
    if (acc) {
      setFundedFirm(acc.firm || '');
      setFundedSize(acc.account_size || '');
      setFundedProfitTarget(acc.profit_target != null ? String(acc.profit_target) : '');
      setFundedMaxDD(acc.max_drawdown != null ? String(acc.max_drawdown) : '');
    }
  };

  // ===== Tick clock every second when session active =====
  useEffect(() => {
    if (!session) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [session]);

  // ===== Login flow =====
  const handleLogin = async () => {
    if (!loginEmail.trim() || !loginPassword) { toast.error('Enter email and password'); return; }
    if (!captchaToken) { toast.error('Please complete the human verification'); return; }
    setLoggingIn(true);
    try {
      const res = await fetch(`${API}/guest/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: loginEmail.trim().toLowerCase(),
          password: loginPassword,
          captcha_token: captchaToken
        })
      });
      const data = await res.json();
      if (res.ok && data.token) {
        localStorage.setItem('guestToken', data.token);
        setToken(data.token);
        setUser({ email: data.email, name: data.name });
        toast.success(`Welcome${data.name ? ', ' + data.name : ''}!`);
      } else {
        toast.error(data.detail || data.message || 'Login failed');
        // Reset captcha on failure so user can re-verify
        if (recaptchaRef.current) recaptchaRef.current.reset();
        setCaptchaToken(null);
      }
    } catch (e) {
      toast.error('Login error');
      if (recaptchaRef.current) recaptchaRef.current.reset();
      setCaptchaToken(null);
    } finally { setLoggingIn(false); }
  };

  // ===== Session control =====
  const startSession = async () => {
    try {
      // Fetch best-setup nudge in parallel
      const nudgePromise = fetch(`${API}/scorecard/best-setup?token=${token}`)
        .then(r => r.json()).catch(() => null);

      const res = await fetch(`${API}/scorecard/session/start?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          trading_style: tradingStyle,
          session_goal: sessionGoal,
          max_losses: parseInt(maxLosses) || null,
          account_type: accountType,
          // Link to the saved account regardless of class (eval OR funded)
          funded_account_id: selectedFundedAccountId || null,
          funded_firm: fundedFirm.trim() || null,
          funded_account_size: fundedSize.trim() || null,
          funded_profit_target: fundedProfitTarget ? parseFloat(fundedProfitTarget) : null,
          funded_max_drawdown: fundedMaxDD ? parseFloat(fundedMaxDD) : null,
        })
      });
      const data = await res.json();
      if (res.status === 403) { setAuthError(data.detail); return; }
      if (data.success) {
        setSession(data.session);
        setTrades([]);
        setDismissedPokes(new Set());
        toast.success(`Live session started — ${STYLE_DETAILS[tradingStyle].label}`);
        const nudgeData = await nudgePromise;
        if (nudgeData && nudgeData.success && (nudgeData.best || nudgeData.last_win)) {
          setBestSetupNudge(nudgeData);
        }
      } else {
        toast.error(data.message || 'Failed to start session');
      }
    } catch (e) { toast.error('Could not start session'); }
  };

  const endSession = async () => {
    if (!session) return;
    setConfirmEnd(true);
  };

  const _doEndSession = async () => {
    if (!session) return;
    setEndingSession(true);
    try {
      const res = await fetch(`${API}/scorecard/session/${session.id}/end?token=${token}`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        playChime();
        toast.success('Session ended. AI Coach is reviewing…');
        setEndModal({ session: data.session, trades: data.trades, stats: data.stats });
        setSession(null);
        setTrades([]);
        loadHistory();
        loadAggStats();
        loadProfile();
        loadFundedAccounts();
        // Auto-status events from the linked account
        const events = data.account_events && data.account_events.events;
        if (Array.isArray(events)) {
          events.forEach(ev => {
            if (ev.kind === 'target_hit_eval')
              toast.success('🎉 EVAL OBJECTIVE REACHED — promote it to Funded!', { duration: 8000 });
            else if (ev.kind === 'target_hit_funded')
              toast.success('💰 Funded payout target hit. Lock it in & request payout!', { duration: 8000 });
            else if (ev.kind === 'blown')
              toast.error('❌ Account blown — max drawdown breached. Auto-marked as blown.', { duration: 8000 });
          });
        }
      }
    } catch (e) { toast.error('Could not end session'); }
    finally {
      setEndingSession(false);
      setConfirmEnd(false);
    }
  };

  // ===== Trade actions =====
  const openExecModal = () => {
    setExecSetups([]); setExecEmotion(5); setExecImpulse(3);
    setExecPhysical('focused');
    setExecInstrument(''); setExecNotes('');
    setShowExecModal(true);
  };

  const submitExecuteTrade = async () => {
    setSubmittingTrade(true);
    try {
      const res = await fetch(`${API}/scorecard/session/${session.id}/trade/execute?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          setup_tags: execSetups,
          emotion_rating: execEmotion,
          impulse_rating: execImpulse,
          physical_state: execPhysical,
          pre_notes: execNotes,
          instrument: execInstrument
        })
      });
      const data = await res.json();
      if (data.success) {
        setTrades([...trades, data.trade]);
        setShowExecModal(false);
        playChime();
        speak('Order filled');
        toast.success(`Trade #${trades.length + 1} logged`);
        if (execSetups.length === 0) toast.warning('No setup selected — flagged as impulsive');
      } else {
        toast.error('Failed to log trade');
      }
    } catch (e) { toast.error('Trade execute failed'); }
    finally { setSubmittingTrade(false); }
  };

  const openCloseModal = (trade) => {
    setCloseModalTrade(trade);
    setCloseOutcome('win'); setClosePnl(''); setCloseNotes(''); setClosePartial(false); setCloseReason('');
  };

  const submitCloseTrade = async () => {
    if (closePnl === '' || isNaN(parseFloat(closePnl))) { toast.error('Enter PnL amount'); return; }
    setSubmittingClose(true);
    try {
      const res = await fetch(`${API}/scorecard/session/${session.id}/trade/${closeModalTrade.id}/close?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          outcome: closeOutcome,
          pnl: parseFloat(closePnl),
          post_notes: closeNotes,
          partial_taken: closePartial,
          close_reason: closeReason
        })
      });
      const data = await res.json();
      if (data.success) {
        setTrades(trades.map(t => t.id === closeModalTrade.id ? data.trade : t));
        setCloseModalTrade(null);
        playChime();
        const phrase = closeOutcome === 'win'
          ? 'Order closed. Trade won.'
          : closeOutcome === 'loss'
            ? 'Order closed. Trade stopped out.'
            : 'Order closed at breakeven.';
        speak(phrase);
        const verb = closeOutcome === 'win' ? '🎉 Win' : closeOutcome === 'loss' ? '😔 Loss' : '😐 Breakeven';
        toast.success(`${verb} · PnL ${parseFloat(closePnl).toFixed(2)}`);
        if (data.ai_verdict) setTradeVerdict({ trade: data.trade, ...data.ai_verdict });

        // Live-update the linked Eval/Funded account snapshot in the dropdown
        if (data.account_update && data.account_update.account) {
          setFundedAccounts(accs => accs.map(a =>
            a.id === data.account_update.account.id ? data.account_update.account : a
          ));
          // Auto-status events (target hit / DD breached) fired ON this trade
          const accEvents = data.account_update.events || [];
          accEvents.forEach(ev => {
            if (ev.kind === 'target_hit_eval')
              toast.success('🎉 EVAL OBJECTIVE REACHED — promote it to Funded!', { duration: 8000 });
            else if (ev.kind === 'target_hit_funded')
              toast.success('💰 Funded payout target hit. Lock it in & request payout!', { duration: 8000 });
            else if (ev.kind === 'blown')
              toast.error('❌ Account blown — max drawdown breached. Auto-marked as blown.', { duration: 8000 });
          });
        }

        // Funded milestone pings → coach posted in chat
        if (Array.isArray(data.milestones) && data.milestones.length > 0) {
          await loadChat(session.id);
          data.milestones.forEach(m => {
            const tone = m.kind.startsWith('dd_') ? 'error' : m.kind === 'payout_hit' ? 'success' : 'info';
            const icon = m.kind === 'payout_hit' ? '🎯'
              : m.kind === 'dd_90' ? '🛑'
              : m.kind === 'dd_75' ? '⚠️'
              : m.kind === 'dd_50' ? '📉'
              : m.kind === 'payout_75' ? '💪'
              : '📈';
            toast[tone](`${icon} ${m.label} — Coach posted in chat`);
          });
          if (!chatOpen) setChatUnread(u => u + data.milestones.length);
        }
      }
    } catch (e) { toast.error('Close failed'); }
    finally { setSubmittingClose(false); }
  };

  // ===== Cancel session =====
  const submitCancelSession = async () => {
    if (!session) return;
    if (!cancelReason.trim()) { toast.error('Please tell the coach why you are ending early'); return; }
    setCancelling(true);
    try {
      const res = await fetch(`${API}/scorecard/session/${session.id}/cancel?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cancel_reason: cancelReason.trim() })
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Session cancelled.');
        setShowCancelModal(false);
        setCancelReason('');
        setSession(null);
        setTrades([]);
        // Show AI verdict in end-modal-style view
        setEndModal({
          session: data.session,
          trades: [],
          stats: data.stats,
          cancelled: true,
          verdict: data.ai_verdict
        });
        loadHistory();
        loadAggStats();
      } else {
        toast.error(data.message || 'Failed to cancel');
      }
    } catch (e) { toast.error('Cancel failed'); }
    finally { setCancelling(false); }
  };

  // ===== Manual trade import =====
  const loadManualTrades = async () => {
    try {
      const res = await fetch(`${API}/scorecard/trades/manual?token=${token}&limit=50`);
      const data = await res.json();
      if (data.success) setManualTrades(data.trades || []);
    } catch (e) { /* ignore */ }
  };

  const submitManualTrade = async () => {
    if (!manualDate || !manualInstrument.trim() || manualPnl === '') {
      toast.error('Fill date, asset and PnL'); return;
    }
    setSubmittingManual(true);
    try {
      const res = await fetch(`${API}/scorecard/trade/manual?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          trade_date: manualDate,
          instrument: manualInstrument.trim().toUpperCase(),
          pnl: parseFloat(manualPnl),
          outcome: manualOutcome,
          setup_tags: manualSetups,
          reason: manualReason,
          session_label: manualSession,
        })
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Manual trade imported');
        setManualTrades([data.trade, ...manualTrades]);
        setManualInstrument(''); setManualPnl(''); setManualReason(''); setManualSession('');
        setManualSetups([]); setManualOutcome('win');
        loadAggStats();
      } else {
        toast.error('Import failed');
      }
    } catch (e) { toast.error('Import failed'); }
    finally { setSubmittingManual(false); }
  };

  const deleteManualTrade = async (tradeId) => {
    try {
      const res = await fetch(`${API}/scorecard/trade/manual/${tradeId}?token=${token}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        setManualTrades(manualTrades.filter(t => t.id !== tradeId));
        toast.success('Removed');
        loadAggStats();
      }
    } catch (e) { toast.error('Delete failed'); }
  };

  useEffect(() => {
    if (token && manualOpen && manualTrades.length === 0) loadManualTrades();
  }, [token, manualOpen]);  // eslint-disable-line

  // ===== AI Coach Chat =====
  const loadChat = async (sessionId) => {
    try {
      const res = await fetch(`${API}/scorecard/session/${sessionId}/chat?token=${token}`);
      const data = await res.json();
      if (data.success) setChatMessages(data.messages || []);
    } catch (e) { /* ignore */ }
  };

  // Auto-load chat when session becomes active + schedule 2-min proactive opener
  useEffect(() => {
    if (session?.id && token) {
      loadChat(session.id);
      // Schedule proactive opener at +2 minutes if user hasn't messaged yet
      const sessionId = session.id;
      const timer = setTimeout(async () => {
        try {
          const r = await fetch(`${API}/scorecard/session/${sessionId}/chat/greet?token=${token}`, { method: 'POST' });
          const d = await r.json();
          if (d.success && !d.skipped) {
            await loadChat(sessionId);
            if (!chatOpen) setChatUnread(u => u + 1);
            toast.info('💬 Your AI Coach has a message for you', { duration: 4000 });
          }
        } catch (e) { /* ignore */ }
      }, 2 * 60 * 1000);
      return () => clearTimeout(timer);
    } else {
      setChatMessages([]);
      setChatOpen(false);
    }
  }, [session?.id, token]);  // eslint-disable-line

  // Auto-scroll chat to bottom on new message
  useEffect(() => {
    if (chatOpen && chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [chatMessages, chatOpen]);

  // Reset unread badge when chat is opened
  useEffect(() => {
    if (chatOpen) setChatUnread(0);
  }, [chatOpen]);

  const onPickChatImage = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error('Image must be under 8MB'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      // Compress / resize via canvas so the payload stays small
      const img = new window.Image();
      img.onload = () => {
        try {
          const maxW = 1280;
          const scale = Math.min(1, maxW / img.width);
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(img.width * scale);
          canvas.height = Math.round(img.height * scale);
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          const compressed = canvas.toDataURL('image/jpeg', 0.75);
          setChatImage(compressed);
        } catch (err) {
          setChatImage(ev.target.result); // fallback to original
        }
      };
      img.onerror = () => setChatImage(ev.target.result);
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
    // Clear file input so the same file can be re-picked
    if (chatFileRef.current) chatFileRef.current.value = '';
  };

  const sendChat = async () => {
    if (!session?.id) return;
    const content = chatInput.trim();
    if (!content && !chatImage) return;
    setChatSending(true);
    // Optimistic user bubble (carry the image so it renders immediately)
    const optimisticUser = {
      id: `tmp-${Date.now()}`,
      role: 'user',
      content,
      has_image: !!chatImage,
      image_base64: chatImage || null,
      ts: new Date().toISOString(),
      _pending: true
    };
    setChatMessages(prev => [...prev, optimisticUser]);
    setChatInput('');
    const imgToSend = chatImage;
    setChatImage(null);
    try {
      const res = await fetch(`${API}/scorecard/session/${session.id}/chat?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content, image_base64: imgToSend })
      });
      const data = await res.json();
      if (data.success) {
        await loadChat(session.id);
        if (!chatOpen) setChatUnread(u => u + 1);
      } else {
        toast.error('Coach is offline. Try again in a moment.');
        setChatMessages(prev => prev.filter(m => m.id !== optimisticUser.id));
      }
    } catch (e) {
      toast.error('Chat failed');
      setChatMessages(prev => prev.filter(m => m.id !== optimisticUser.id));
    } finally { setChatSending(false); }
  };

  // ===== Email reports =====
  const sendReport = async (period) => {
    setEmailing(true);
    try {
      const res = await fetch(`${API}/scorecard/report/email?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ period })
      });
      const data = await res.json();
      if (data.success) toast.success(data.message);
      else toast.error(data.message || 'Failed to send report');
    } catch (e) { toast.error('Could not send report'); }
    finally { setEmailing(false); }
  };

  // ===== Live stats (during active session) =====
  const liveStats = useMemo(() => {
    const closed = trades.filter(t => t.closed_at);
    const wins = closed.filter(t => t.outcome === 'win');
    const losses = closed.filter(t => t.outcome === 'loss');
    const totalPnl = closed.reduce((sum, t) => sum + (parseFloat(t.pnl) || 0), 0);
    const winRate = closed.length > 0 ? (wins.length / closed.length * 100) : 0;
    const withSetups = trades.filter(t => (t.setup_tags || []).length > 0);
    const discipline = trades.length > 0 ? (withSetups.length / trades.length * 100) : 100;
    return {
      total: trades.length, open: trades.length - closed.length,
      wins: wins.length, losses: losses.length, winRate, totalPnl, discipline
    };
  }, [trades]);

  // ===== Smart pokes (client-side) =====
  const pokes = useMemo(() => {
    if (!session) return [];
    const style = STYLE_DETAILS[session.trading_style] || STYLE_DETAILS.scalp;
    const sessionStartMs = new Date(session.started_at).getTime();
    const elapsedMin = (now - sessionStartMs) / 60000;
    const result = [];

    // Risk guardrail: hit max losses
    const lossCount = trades.filter(t => t.outcome === 'loss').length;
    if (session.max_losses && lossCount >= session.max_losses) {
      result.push({
        id: 'risk-stop', tone: 'danger', icon: '🛑',
        title: 'Risk guardrail hit',
        msg: `You've reached ${lossCount} losses (limit ${session.max_losses}). Step away. Tomorrow is another setup.`
      });
    }

    // No trade after N minutes (and no open trade right now)
    const openTrade = trades.find(t => !t.closed_at);
    if (!openTrade && trades.length === 0 && elapsedMin >= style.noTradePokeMin) {
      result.push({
        id: 'no-trade', tone: 'info', icon: '👀',
        title: 'No setup yet?',
        msg: `${Math.floor(elapsedMin)} min in. If you don't see a valid ${style.label.toLowerCase()} setup, close the chart and come back later.`
      });
    }

    // Open trade poke: take partial / monitor
    if (openTrade) {
      const tradeElapsedMin = (now - new Date(openTrade.executed_at).getTime()) / 60000;
      if (tradeElapsedMin >= style.openTradePokeMin) {
        result.push({
          id: `open-${openTrade.id}`, tone: 'info', icon: '💰',
          title: 'Open trade reminder',
          msg: `Trade running ${Math.floor(tradeElapsedMin)} min. If it's in profit, consider taking partial.`
        });
      }
    }

    // High emotion warning (recent trade)
    const recent = trades.slice(-3);
    const avgEmotionRecent = recent.length > 0 ? recent.reduce((s, t) => s + (t.emotion_rating || 0), 0) / recent.length : 0;
    if (recent.length >= 2 && avgEmotionRecent >= 7) {
      result.push({
        id: 'tilt', tone: 'warning', icon: '🧘',
        title: 'Emotional state high',
        msg: `Your last few trades show high anxiety (${avgEmotionRecent.toFixed(1)}/10). Take 5 min away from the chart.`
      });
    }

    // Impulsive streak warning
    const impulsive = recent.filter(t => (t.setup_tags || []).length === 0 || (t.impulse_rating || 0) >= 7);
    if (impulsive.length >= 2) {
      result.push({
        id: 'impulse', tone: 'warning', icon: '⚠️',
        title: 'Impulse pattern detected',
        msg: `Multiple recent trades flagged impulsive. Slow down. Wait for a clean setup with a tag.`
      });
    }

    return result.filter(p => !dismissedPokes.has(p.id));
  }, [session, trades, now, dismissedPokes]);

  // ===== Render guards =====
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-amber-400 animate-spin" />
      </div>
    );
  }

  if (!token || authError) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center p-4">
        <Toaster position="top-right" richColors />
        <div className="max-w-md w-full">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold bg-gradient-to-r from-amber-300 to-yellow-400 bg-clip-text text-transparent">My Personal AI Session Coach</h1>
            <p className="text-amber-200/60 text-xs tracking-[3px] uppercase mt-2">FORTHEWISEPRO</p>
          </div>
          {authError && (
            <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-4 mb-4 text-rose-200 text-sm">
              <AlertTriangle className="w-4 h-4 inline mr-1" /> {authError}
            </div>
          )}
          <Card className="bg-zinc-900/70 border border-amber-500/20">
            <CardHeader>
              <CardTitle className="text-white">Sign in</CardTitle>
              <CardDescription className="text-gray-400">Use your FORTHEWISEPRO credentials. Active subscription required.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Input value={loginEmail} onChange={e => setLoginEmail(e.target.value)} placeholder="email@example.com"
                className="bg-zinc-800 border-amber-500/20 text-white" data-testid="sc-login-email" />
              <Input value={loginPassword} onChange={e => setLoginPassword(e.target.value)} type="password" placeholder="password"
                className="bg-zinc-800 border-amber-500/20 text-white"
                onKeyDown={e => e.key === 'Enter' && handleLogin()} data-testid="sc-login-password" />
              <div className="flex justify-center py-2" data-testid="sc-login-recaptcha">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={RECAPTCHA_SITE_KEY}
                  onChange={(t) => setCaptchaToken(t)}
                  onExpired={() => setCaptchaToken(null)}
                  theme="dark"
                />
              </div>
              <Button onClick={handleLogin} disabled={loggingIn || !captchaToken}
                className="w-full bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-amber-300 hover:to-yellow-400 text-black font-bold"
                data-testid="sc-login-btn">
                {loggingIn ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enter Coach'}
              </Button>
              <p className="text-xs text-center text-gray-500">No subscription? <a href="/" className="text-amber-300 underline">Subscribe</a></p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const sessionElapsedSec = session ? Math.floor((now - new Date(session.started_at).getTime()) / 1000) : 0;
  const style = session ? (STYLE_DETAILS[session.trading_style] || STYLE_DETAILS.scalp) : null;

  return (
    <div className="min-h-screen bg-black text-white">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <header className="sticky top-0 z-40 bg-black/95 backdrop-blur-md border-b border-amber-500/20">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-yellow-600 flex items-center justify-center text-black font-black ring-2 ring-amber-400/40">
              <Brain className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-bold bg-gradient-to-r from-amber-300 to-yellow-400 bg-clip-text text-transparent leading-tight">My Personal AI Session Coach</h1>
              <p className="text-[10px] text-amber-200/60 tracking-[2px] uppercase">FORTHEWISEPRO Trader Scorecard</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/portal')} className="text-amber-200/70 hover:text-amber-200 hidden sm:inline-flex">
              Portal
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { localStorage.removeItem('guestToken'); setToken(''); }} className="text-gray-400 hover:text-rose-300" data-testid="sc-logout-btn">
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6">
        {/* ============ Trading Profile (top widget) ============ */}
        {profileSummary && (
          <Card className={`border-2 ${profileSummary.status === 'profitable' ? 'border-emerald-400/60 bg-gradient-to-br from-emerald-500/10 to-zinc-900' : profileSummary.status === 'drawdown' ? 'border-rose-400/60 bg-gradient-to-br from-rose-500/10 to-zinc-900' : profileSummary.status === 'breakeven' ? 'border-amber-400/60 bg-gradient-to-br from-amber-500/10 to-zinc-900' : 'border-zinc-700 bg-zinc-900'}`} data-testid="sc-profile-widget">
            <CardContent className="p-4 sm:p-5">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${profileSummary.status === 'profitable' ? 'bg-emerald-500/30' : profileSummary.status === 'drawdown' ? 'bg-rose-500/30' : 'bg-amber-500/20'}`}>
                    {profileSummary.status === 'profitable' ? <TrendingUp className="w-6 h-6 text-emerald-300" /> :
                      profileSummary.status === 'drawdown' ? <TrendingDown className="w-6 h-6 text-rose-300" /> :
                      <Activity className="w-6 h-6 text-amber-300" />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-[2px] text-amber-300/80 font-semibold">Prop-firm Trading Profile</p>
                    <p className={`text-lg sm:text-xl font-black ${profileSummary.net_pnl >= 0 ? 'text-emerald-300' : 'text-rose-300'}`} data-testid="sc-profile-net">
                      {profileSummary.net_pnl >= 0 ? '+' : ''}${profileSummary.net_pnl.toLocaleString()}
                      <span className="text-xs ml-2 text-gray-400 font-normal">({profileSummary.status_label})</span>
                    </p>
                    <p className="text-[11px] text-gray-400">
                      💸 Spent <span className="text-rose-300 font-mono">${profileSummary.total_expenses.toLocaleString()}</span>
                      {' · '}💰 Payouts <span className="text-emerald-300 font-mono">${profileSummary.total_payouts.toLocaleString()}</span>
                      {profileSummary.roi_pct !== 0 && <span> · ROI {profileSummary.roi_pct > 0 ? '+' : ''}{profileSummary.roi_pct}%</span>}
                      {profileSummary.accounts_blown > 0 && <span> · 🪦 {profileSummary.accounts_blown} blown</span>}
                    </p>
                  </div>
                </div>
                <div className="flex gap-1.5 flex-wrap justify-center">
                  <Button size="sm" onClick={() => { setProfForm({ ...profForm, amount: '', firm: '', account_size: '', type: 'eval_purchase', notes: '' }); setShowAddExpense(true); }}
                    className="bg-rose-500/20 hover:bg-rose-500/30 text-rose-200 border border-rose-400/30 text-xs h-8" data-testid="sc-add-expense-btn">
                    💸 Add expense
                  </Button>
                  <Button size="sm" onClick={() => { setProfForm({ ...profForm, amount: '', firm: '', account_size: '', notes: '' }); setShowAddPayout(true); }}
                    className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 border border-emerald-400/30 text-xs h-8" data-testid="sc-add-payout-btn">
                    💰 Add payout
                  </Button>
                  <Button size="sm" onClick={() => { setProfForm({ ...profForm, firm: '', account_size: '', account_type: 'funded', notes: '' }); setShowAddBlown(true); }}
                    className="bg-zinc-700/40 hover:bg-zinc-700/60 text-gray-300 border border-zinc-600 text-xs h-8" data-testid="sc-add-blown-btn">
                    🪦 Blown account
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setProfileOpen(!profileOpen)}
                    className="text-amber-300 hover:bg-amber-500/10 text-xs h-8" data-testid="sc-profile-toggle">
                    {profileOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              {profileOpen && profile && (
                <div className="mt-4 pt-4 border-t border-white/5 space-y-4">
                  {/* Eval accounts list */}
                  {(() => {
                    const evals = fundedAccounts.filter(a => (a.account_class || 'funded') === 'eval');
                    const fundeds = fundedAccounts.filter(a => (a.account_class || 'funded') === 'funded');
                    return (
                      <>
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-bold uppercase tracking-wider text-amber-300">🎓 Eval accounts ({evals.length})</p>
                            <Button size="sm" onClick={() => openAddFundedAccount('eval')}
                              className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-400/30 text-xs h-7"
                              data-testid="sc-eval-add-btn">
                              ＋ Add eval
                            </Button>
                          </div>
                          {evals.length === 0 ? (
                            <p className="text-[11px] text-gray-500 italic">No eval accounts saved.</p>
                          ) : (
                            <div className="grid sm:grid-cols-2 gap-2">
                              {evals.map(a => (
                                <AccountCard key={a.id} a={a} klass="eval"
                                  onEdit={() => openEditFundedAccount(a)}
                                  onDelete={() => deleteFundedAccount(a.id)}
                                  onView={() => openAccountDetails(a)}
                                  onPromote={() => promoteEvalToFunded(a.id)} />
                              ))}
                            </div>
                          )}
                        </div>

                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-bold uppercase tracking-wider text-emerald-300">💼 Funded accounts ({fundeds.length})</p>
                            <Button size="sm" onClick={() => openAddFundedAccount('funded')}
                              className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 border border-emerald-400/30 text-xs h-7"
                              data-testid="sc-funded-add-btn">
                              ＋ Add funded
                            </Button>
                          </div>
                          {fundeds.length === 0 ? (
                            <p className="text-[11px] text-gray-500 italic">No funded accounts saved yet.</p>
                          ) : (
                            <div className="grid sm:grid-cols-2 gap-2">
                              {fundeds.map(a => (
                                <AccountCard key={a.id} a={a} klass="funded"
                                  onEdit={() => openEditFundedAccount(a)}
                                  onDelete={() => deleteFundedAccount(a.id)}
                                  onView={() => openAccountDetails(a)} />
                              ))}
                            </div>
                          )}
                        </div>
                      </>
                    );
                  })()}

                  <div className="grid md:grid-cols-3 gap-3 pt-3 border-t border-white/5">
                    <ProfileList title="💸 Expenses" items={profile.expenses} kind="expense" color="rose" onDelete={deleteProfileEntry} />
                    <ProfileList title="💰 Payouts" items={profile.payouts} kind="payout" color="emerald" onDelete={deleteProfileEntry} />
                    <ProfileList title="🪦 Blown accounts" items={profile.blown_accounts} kind="blown" color="zinc" onDelete={deleteProfileEntry} amountKey={null} />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Session Control */}
        {!session ? (
          <Card className="bg-gradient-to-br from-zinc-900 to-black border border-amber-500/20" data-testid="sc-start-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white"><Play className="w-5 h-5 text-amber-400" /> Start a Live Session</CardTitle>
              <CardDescription className="text-gray-400">Choose your style. The coach will track everything from here.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Account type picker (Eval vs Funded) */}
              <div>
                <Label className="text-xs text-amber-300 block mb-2">Which account are you trading on?</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => { setAccountType('eval'); setSelectedFundedAccountId(''); setFundedFirm(''); setFundedSize(''); setFundedProfitTarget(''); setFundedMaxDD(''); }}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${accountType === 'eval' ? 'border-amber-400 bg-amber-500/10 text-white' : 'border-zinc-800 bg-zinc-900 text-gray-400 hover:border-amber-500/40'}`}
                    data-testid="sc-account-eval"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xl">🎓</span>
                      <div>
                        <div className="font-bold text-sm">Eval</div>
                        <div className="text-[10px] opacity-70">Practice — won't move your profile PnL</div>
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => { setAccountType('funded'); setSelectedFundedAccountId(''); setFundedFirm(''); setFundedSize(''); setFundedProfitTarget(''); setFundedMaxDD(''); }}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${accountType === 'funded' ? 'border-emerald-400 bg-emerald-500/10 text-white' : 'border-zinc-800 bg-zinc-900 text-gray-400 hover:border-emerald-500/40'}`}
                    data-testid="sc-account-funded"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xl">💼</span>
                      <div>
                        <div className="font-bold text-sm">Funded</div>
                        <div className="text-[10px] opacity-70">Real account — coach guards drawdown</div>
                      </div>
                    </div>
                  </button>
                </div>
                {accountType === 'funded' && (
                  <div className="mt-3 space-y-2">
                    {(() => {
                      const evalOrFundedAccounts = fundedAccounts.filter(a => (a.account_class || 'funded') === 'funded' && a.status === 'active');
                      return evalOrFundedAccounts.length > 0 ? (
                        <div>
                          <Label className="text-xs text-amber-300 block mb-1">Which funded account?</Label>
                          <div className="flex gap-2">
                            <select
                              value={selectedFundedAccountId}
                              onChange={e => onPickFundedAccount(e.target.value)}
                              className="flex-1 bg-zinc-900 border border-emerald-500/30 text-white rounded-md px-3 py-2 text-sm"
                              data-testid="sc-funded-account-dropdown"
                            >
                              <option value="">— Quick setup (not saved) —</option>
                              {evalOrFundedAccounts.map(a => (
                                <option key={a.id} value={a.id}>
                                  {a.name} {a.firm ? `· ${a.firm}` : ''} {a.account_size ? `· ${a.account_size}` : ''}
                                  {a.current_pnl != null ? ` · PnL ${a.current_pnl >= 0 ? '+' : ''}$${a.current_pnl.toFixed(0)}` : ''}
                                </option>
                              ))}
                            </select>
                            <Button size="sm" type="button" onClick={() => openAddFundedAccount('funded')}
                              className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 border border-emerald-400/30 text-xs h-9"
                              data-testid="sc-funded-account-new">
                              ＋ New
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <Button type="button" onClick={() => openAddFundedAccount('funded')}
                          variant="outline"
                          className="w-full border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 text-xs"
                          data-testid="sc-funded-account-create-first">
                          ＋ Save your first funded account
                        </Button>
                      );
                    })()}
                    <div className="grid grid-cols-2 gap-2">
                      <Input value={fundedFirm} onChange={e => setFundedFirm(e.target.value)} placeholder="Prop firm (FTMO, Topstep…)"
                        className="bg-zinc-900 border-emerald-500/20 text-white text-sm" data-testid="sc-funded-firm" />
                      <Input value={fundedSize} onChange={e => setFundedSize(e.target.value)} placeholder="Account size ($50K…)"
                        className="bg-zinc-900 border-emerald-500/20 text-white text-sm" data-testid="sc-funded-size" />
                      <Input type="number" value={fundedProfitTarget} onChange={e => setFundedProfitTarget(e.target.value)} placeholder="$ to next payout (optional)"
                        className="bg-zinc-900 border-emerald-500/20 text-white text-sm" data-testid="sc-funded-target" />
                      <Input type="number" value={fundedMaxDD} onChange={e => setFundedMaxDD(e.target.value)} placeholder="Max DD $ (optional)"
                        className="bg-zinc-900 border-emerald-500/20 text-white text-sm" data-testid="sc-funded-maxdd" />
                    </div>
                  </div>
                )}
                {accountType === 'eval' && (
                  <div className="mt-3 space-y-2">
                    {(() => {
                      const evalAccounts = fundedAccounts.filter(a => (a.account_class || 'funded') === 'eval' && a.status === 'active');
                      return evalAccounts.length > 0 ? (
                        <div>
                          <Label className="text-xs text-amber-300 block mb-1">Which eval account?</Label>
                          <div className="flex gap-2">
                            <select
                              value={selectedFundedAccountId}
                              onChange={e => onPickFundedAccount(e.target.value)}
                              className="flex-1 bg-zinc-900 border border-amber-500/30 text-white rounded-md px-3 py-2 text-sm"
                              data-testid="sc-eval-account-dropdown"
                            >
                              <option value="">— Quick eval (not tracked) —</option>
                              {evalAccounts.map(a => (
                                <option key={a.id} value={a.id}>
                                  🎓 {a.name} {a.firm ? `· ${a.firm}` : ''} {a.account_size ? `· ${a.account_size}` : ''}
                                  {a.current_pnl != null ? ` · PnL ${a.current_pnl >= 0 ? '+' : ''}$${a.current_pnl.toFixed(0)}` : ''}
                                </option>
                              ))}
                            </select>
                            <Button size="sm" type="button" onClick={() => openAddFundedAccount('eval')}
                              className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-400/30 text-xs h-9"
                              data-testid="sc-eval-account-new">
                              ＋ New
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <Button type="button" onClick={() => openAddFundedAccount('eval')}
                          variant="outline"
                          className="w-full border-amber-500/30 text-amber-300 hover:bg-amber-500/10 text-xs"
                          data-testid="sc-eval-account-create-first">
                          ＋ Save an eval account to track progress
                        </Button>
                      );
                    })()}
                    {selectedFundedAccountId && (
                      <div className="grid grid-cols-2 gap-2">
                        <Input value={fundedFirm} onChange={e => setFundedFirm(e.target.value)} placeholder="Prop firm"
                          className="bg-zinc-900 border-amber-500/20 text-white text-sm" />
                        <Input value={fundedSize} onChange={e => setFundedSize(e.target.value)} placeholder="Account size"
                          className="bg-zinc-900 border-amber-500/20 text-white text-sm" />
                        <Input type="number" value={fundedProfitTarget} onChange={e => setFundedProfitTarget(e.target.value)} placeholder="Profit target $"
                          className="bg-zinc-900 border-amber-500/20 text-white text-sm" />
                        <Input type="number" value={fundedMaxDD} onChange={e => setFundedMaxDD(e.target.value)} placeholder="Max DD $"
                          className="bg-zinc-900 border-amber-500/20 text-white text-sm" />
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-3 gap-2">
                {Object.entries(STYLE_DETAILS).map(([k, v]) => (
                  <button
                    key={k}
                    onClick={() => setTradingStyle(k)}
                    className={`p-4 rounded-xl border-2 transition-all ${tradingStyle === k ? `border-amber-400 bg-gradient-to-br ${v.color} text-black font-bold shadow-lg shadow-amber-500/30` : 'border-zinc-800 bg-zinc-900 text-gray-400 hover:border-amber-500/40'}`}
                    data-testid={`sc-style-${k}`}
                  >
                    <div className="text-2xl mb-1">{v.icon}</div>
                    <div className="text-sm">{v.label}</div>
                    <div className="text-[10px] mt-1 opacity-70">Poke @ {v.noTradePokeMin}m no-trade</div>
                  </button>
                ))}
              </div>
              <div>
                <Label className="text-xs text-amber-300">Session goal (optional)</Label>
                <Input value={sessionGoal} onChange={e => setSessionGoal(e.target.value)}
                  placeholder="e.g. 2 quality trend setups, +1R minimum"
                  className="bg-zinc-900 border-amber-500/20 text-white" data-testid="sc-goal-input" />
              </div>
              <div>
                <Label className="text-xs text-amber-300">Max losses (risk guardrail)</Label>
                <Input type="number" min="1" max="10" value={maxLosses} onChange={e => setMaxLosses(e.target.value)}
                  className="bg-zinc-900 border-amber-500/20 text-white w-24" data-testid="sc-maxloss-input" />
              </div>
              <Button onClick={startSession}
                className="w-full bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-amber-300 hover:to-yellow-400 text-black font-bold py-6 text-lg shadow-[0_0_30px_rgba(251,191,36,0.4)]"
                data-testid="sc-start-session-btn">
                <Play className="w-5 h-5 mr-2" /> Toggle Live Session ON
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className={`bg-gradient-to-br from-zinc-900 to-black border-2 border-amber-400 shadow-[0_0_30px_rgba(251,191,36,0.25)]`} data-testid="sc-active-card">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-rose-500 animate-pulse"></span>
                    <span className="text-amber-300 text-xs font-bold tracking-[3px] uppercase">LIVE SESSION · {style.label}</span>
                  </div>
                  <div className="text-5xl font-mono font-black text-white mt-1" data-testid="sc-stopwatch">{formatTime(sessionElapsedSec)}</div>
                  {session.session_goal && <p className="text-xs text-amber-200/70 mt-1">🎯 {session.session_goal}</p>}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={openExecModal}
                    className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-white font-bold px-6 py-5"
                    data-testid="sc-exec-trade-btn">
                    <Zap className="w-4 h-4 mr-2" /> Trade Executed
                  </Button>
                  <Button onClick={endSession} variant="outline"
                    className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10 px-6 py-5"
                    data-testid="sc-end-session-btn">
                    <Square className="w-4 h-4 mr-2" /> End Session
                  </Button>
                  <Button onClick={() => { setCancelReason(''); setShowCancelModal(true); }} variant="ghost"
                    className="text-amber-300 hover:bg-amber-500/10 px-4 py-5"
                    data-testid="sc-cancel-session-btn">
                    <X className="w-4 h-4 mr-2" /> Cancel
                  </Button>
                </div>
              </div>

              {/* Live stats strip */}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-6">
                <StatChip label="Trades" value={liveStats.total} />
                <StatChip label="Open" value={liveStats.open} accent="amber" />
                <StatChip label="Win Rate" value={liveStats.total > 0 ? `${Math.round(liveStats.winRate)}%` : '-'} accent={liveStats.winRate >= 50 ? 'emerald' : 'rose'} />
                <StatChip label="PnL" value={liveStats.totalPnl.toFixed(2)} accent={liveStats.totalPnl >= 0 ? 'emerald' : 'rose'} />
                <StatChip label="Discipline" value={`${Math.round(liveStats.discipline)}%`} accent="amber" />
              </div>

              {/* Funded / Eval scoreboard — payout progress / drawdown headroom */}
              {(session.funded_profit_target || session.funded_max_drawdown) && (
                <FundedScoreboard
                  session={session}
                  livePnl={liveStats.totalPnl}
                  linkedAccount={session.funded_account_id ? fundedAccounts.find(a => a.id === session.funded_account_id) : null}
                />
              )}
            </CardContent>
          </Card>
        )}

        {/* Best-setup nudge after starting session */}
        {session && bestSetupNudge && (bestSetupNudge.best || bestSetupNudge.last_win) && (
          <div className="rounded-2xl p-5 bg-gradient-to-br from-amber-500/10 via-yellow-500/5 to-emerald-500/10 border-2 border-amber-400/50 shadow-[0_0_25px_rgba(251,191,36,0.15)] relative" data-testid="sc-best-setup-nudge">
            <button
              onClick={() => setBestSetupNudge(null)}
              className="absolute top-3 right-3 text-amber-200/60 hover:text-amber-200"
              data-testid="sc-best-setup-dismiss"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center shrink-0">
                <Star className="w-6 h-6 text-black" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-amber-300 text-[10px] font-bold tracking-[2px] uppercase">AI Coach · Remember</p>
                <h3 className="text-white font-bold text-base mt-0.5">Stick to what's been working for you</h3>
                <div className="grid sm:grid-cols-2 gap-3 mt-3">
                  {bestSetupNudge.best && (
                    <div className="bg-black/30 border border-emerald-400/30 rounded-lg p-3">
                      <p className="text-[10px] text-emerald-300 uppercase tracking-wider font-semibold">🏆 Your highest win-rate setup</p>
                      <p className="text-white font-bold mt-1">{bestSetupNudge.best.label}</p>
                      <p className="text-xs text-gray-300 mt-0.5">
                        <span className="text-emerald-400 font-bold">{bestSetupNudge.best.win_rate}% win</span>
                        {' · '}{bestSetupNudge.best.wins}/{bestSetupNudge.best.total} trades
                        {' · '}<span className={bestSetupNudge.best.pnl >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
                          {bestSetupNudge.best.pnl >= 0 ? '+' : ''}{bestSetupNudge.best.pnl} PnL
                        </span>
                      </p>
                    </div>
                  )}
                  {bestSetupNudge.last_win && (
                    <div className="bg-black/30 border border-amber-400/30 rounded-lg p-3">
                      <p className="text-[10px] text-amber-300 uppercase tracking-wider font-semibold">🎯 Your last winning trade</p>
                      <p className="text-white font-bold mt-1 line-clamp-1">
                        {(bestSetupNudge.last_win.setup_labels || []).join(' + ') || 'No setup tagged'}
                      </p>
                      <p className="text-xs text-gray-300 mt-0.5">
                        {bestSetupNudge.last_win.instrument && <span className="text-amber-200">{bestSetupNudge.last_win.instrument} · </span>}
                        <span className="text-emerald-400 font-bold">+{bestSetupNudge.last_win.pnl}</span>
                        {bestSetupNudge.last_win.closed_at && <span className="text-gray-500"> · {new Date(bestSetupNudge.last_win.closed_at).toLocaleDateString()}</span>}
                      </p>
                    </div>
                  )}
                </div>
                <p className="text-[11px] text-amber-200/70 mt-3 italic">
                  Wait for these conditions today. Discipline &gt; FOMO.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Smart pokes */}
        {pokes.length > 0 && (
          <div className="space-y-2" data-testid="sc-pokes">
            {pokes.map(p => (
              <div key={p.id} className={`rounded-xl p-4 border flex items-start gap-3 ${p.tone === 'danger' ? 'bg-rose-500/10 border-rose-500/40' : p.tone === 'warning' ? 'bg-amber-500/10 border-amber-500/40' : 'bg-blue-500/10 border-blue-500/40'}`}>
                <div className="text-2xl">{p.icon}</div>
                <div className="flex-1">
                  <p className="font-bold text-white text-sm">{p.title}</p>
                  <p className="text-sm text-gray-300 mt-1">{p.msg}</p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => setDismissedPokes(new Set([...dismissedPokes, p.id]))} className="text-gray-400">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Active trades */}
        {session && trades.length > 0 && (
          <Card className="bg-zinc-900/70 border border-amber-500/15">
            <CardHeader>
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Activity className="w-5 h-5 text-amber-400" /> Session Trades ({trades.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {trades.map((t, i) => {
                const isOpen = !t.closed_at;
                const tradeSec = Math.floor((now - new Date(t.executed_at).getTime()) / 1000);
                const closedSec = t.closed_at ? Math.floor((new Date(t.closed_at).getTime() - new Date(t.executed_at).getTime()) / 1000) : 0;
                const outcomeColor = t.outcome === 'win' ? 'text-emerald-400' : t.outcome === 'loss' ? 'text-rose-400' : 'text-gray-400';
                return (
                  <div key={t.id} className={`rounded-lg border p-3 ${isOpen ? 'border-amber-400/40 bg-amber-500/5' : 'border-zinc-800 bg-zinc-900/50'}`} data-testid={`sc-trade-row-${i}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white font-semibold text-sm">#{i + 1}</span>
                          {t.instrument && <Badge className="bg-zinc-800 text-amber-200 border-amber-500/30 text-[10px]">{t.instrument}</Badge>}
                          {isOpen ? (
                            <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/40 text-[10px] animate-pulse">OPEN · {formatTime(tradeSec)}</Badge>
                          ) : (
                            <Badge className={`text-[10px] border ${t.outcome === 'win' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' : t.outcome === 'loss' ? 'bg-rose-500/20 text-rose-300 border-rose-500/40' : 'bg-zinc-700 text-gray-300 border-zinc-600'}`}>
                              {(t.outcome || '-').toUpperCase()} · {formatTime(closedSec)}
                            </Badge>
                          )}
                          {!isOpen && <span className={`text-sm font-bold ${outcomeColor}`}>{parseFloat(t.pnl) >= 0 ? '+' : ''}{parseFloat(t.pnl).toFixed(2)}</span>}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {(t.setup_tags || []).length === 0 ? (
                            <span className="text-[10px] text-rose-300">⚠️ No setup tag</span>
                          ) : (
                            t.setup_tags.map(tag => {
                              const opt = SETUP_OPTIONS.find(o => o.id === tag);
                              return <span key={tag} className="text-[10px] bg-amber-500/10 text-amber-200 border border-amber-500/20 rounded-full px-2 py-0.5">{opt?.icon} {opt?.label || tag}</span>;
                            })
                          )}
                          <span className="text-[10px] text-gray-500">😌 {t.emotion_rating}/10 · ⚡ {t.impulse_rating}/10</span>
                        </div>
                      </div>
                      {isOpen && (
                        <Button size="sm" onClick={() => openCloseModal(t)}
                          className="bg-rose-500 hover:bg-rose-400 text-white shrink-0"
                          data-testid={`sc-close-trade-${i}`}>
                          Trade Closed
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Aggregate stats */}
        {aggStats && (
          <Card className="bg-zinc-900/70 border border-amber-500/15">
            <CardHeader>
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-amber-400" /> Last 30 Days
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatBox label="Sessions" value={aggStats.sessions_count || 0} />
                <StatBox label="Trades" value={aggStats.trades_count} />
                <StatBox label="Win Rate" value={`${aggStats.win_rate}%`} accent={aggStats.win_rate >= 50 ? 'emerald' : 'rose'} />
                <StatBox label="Net PnL" value={`${aggStats.total_pnl >= 0 ? '+' : ''}${aggStats.total_pnl}`} accent={aggStats.total_pnl >= 0 ? 'emerald' : 'rose'} />
                <StatBox label="Discipline" value={`${aggStats.discipline_score}%`} accent="amber" />
                <StatBox label="Avg Emotion" value={`${aggStats.avg_emotion}/10`} />
                <StatBox label="Avg Impulse" value={`${aggStats.avg_impulse}/10`} />
                <StatBox label="Streak" value={`${aggStats.streak || 0}${aggStats.streak_type ? (aggStats.streak_type === 'win' ? 'W' : 'L') : ''}`} accent={aggStats.streak_type === 'win' ? 'emerald' : 'rose'} />
              </div>

              {Object.keys(aggStats.setup_performance || {}).length > 0 && (
                <div className="mt-4">
                  <p className="text-xs text-amber-300 mb-2 font-semibold uppercase tracking-wider">Setup Performance</p>
                  <div className="space-y-1">
                    {Object.entries(aggStats.setup_performance).sort((a, b) => b[1].win_rate - a[1].win_rate).map(([tag, perf]) => {
                      const opt = SETUP_OPTIONS.find(o => o.id === tag);
                      return (
                        <div key={tag} className="flex items-center justify-between bg-zinc-800/50 rounded px-3 py-2 text-xs">
                          <span className="text-white">{opt?.icon} {opt?.label || tag}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-gray-400">{perf.total} trades</span>
                            <span className={`font-bold ${perf.win_rate >= 50 ? 'text-emerald-400' : 'text-rose-400'}`}>{perf.win_rate.toFixed(0)}% win</span>
                            <span className={`${perf.pnl >= 0 ? 'text-emerald-400' : 'text-rose-400'} font-bold w-16 text-right`}>{perf.pnl >= 0 ? '+' : ''}{perf.pnl.toFixed(2)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="mt-5 flex flex-wrap gap-2 pt-4 border-t border-zinc-800">
                <Button variant="outline" size="sm" onClick={() => sendReport('weekly')} disabled={emailing}
                  className="border-amber-400/40 text-amber-300 hover:bg-amber-500/10" data-testid="sc-email-weekly-btn">
                  {emailing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Mail className="w-3 h-3 mr-1" />}
                  Email Weekly Report
                </Button>
                <Button variant="outline" size="sm" onClick={() => sendReport('monthly')} disabled={emailing}
                  className="border-amber-400/40 text-amber-300 hover:bg-amber-500/10" data-testid="sc-email-monthly-btn">
                  <Calendar className="w-3 h-3 mr-1" /> Email Monthly Report
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Manual Trade Import */}
        <Card className="bg-zinc-900/70 border border-amber-500/15">
          <CardHeader className="cursor-pointer" onClick={() => setManualOpen(!manualOpen)} data-testid="sc-manual-toggle">
            <CardTitle className="text-white text-lg flex items-center justify-between">
              <span className="flex items-center gap-2"><FileDown className="w-5 h-5 text-amber-400" /> Manual Trade Import</span>
              {manualOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </CardTitle>
            <CardDescription className="text-gray-500 text-xs">
              Log trades you took outside the live AI Coach. They'll be folded into your 30-day stats.
            </CardDescription>
          </CardHeader>
          {manualOpen && (
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-amber-300">Date</Label>
                  <Input type="date" value={manualDate} onChange={e => setManualDate(e.target.value)}
                    className="bg-zinc-800 border-amber-500/20 text-white" data-testid="sc-manual-date" />
                </div>
                <div>
                  <Label className="text-xs text-amber-300">Session label (optional)</Label>
                  <Input value={manualSession} onChange={e => setManualSession(e.target.value)}
                    placeholder="London / NY Open / Asia"
                    className="bg-zinc-800 border-amber-500/20 text-white" data-testid="sc-manual-session-label" />
                </div>
                <div>
                  <Label className="text-xs text-amber-300">Asset traded</Label>
                  <Input value={manualInstrument} onChange={e => setManualInstrument(e.target.value)}
                    placeholder="EURUSD, BTCUSD, NAS100..."
                    className="bg-zinc-800 border-amber-500/20 text-white font-mono uppercase"
                    data-testid="sc-manual-instrument" />
                </div>
                <div>
                  <Label className="text-xs text-amber-300">PnL ($)</Label>
                  <Input type="number" step="0.01" value={manualPnl} onChange={e => setManualPnl(e.target.value)}
                    placeholder="42.50 or -25.00"
                    className="bg-zinc-800 border-amber-500/20 text-white font-mono" data-testid="sc-manual-pnl" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-amber-300 block mb-2">Outcome</Label>
                <div className="grid grid-cols-3 gap-2">
                  {['win', 'loss', 'breakeven'].map(o => (
                    <button key={o} type="button" onClick={() => setManualOutcome(o)}
                      className={`py-2 rounded-lg border-2 font-bold text-xs transition-all ${manualOutcome === o ? (o === 'win' ? 'bg-emerald-500 text-white border-emerald-400' : o === 'loss' ? 'bg-rose-500 text-white border-rose-400' : 'bg-zinc-600 text-white border-zinc-500') : 'bg-zinc-800 border-zinc-700 text-gray-400 hover:border-amber-500/40'}`}
                      data-testid={`sc-manual-outcome-${o}`}>
                      {o.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs text-amber-300 block mb-2">Setup(s) used (optional)</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-44 overflow-y-auto pr-1">
                  {SETUP_OPTIONS.map(s => {
                    const active = manualSetups.includes(s.id);
                    return (
                      <button key={s.id} type="button"
                        onClick={() => setManualSetups(active ? manualSetups.filter(x => x !== s.id) : [...manualSetups, s.id])}
                        className={`text-left p-2 rounded-lg border-2 text-[11px] transition ${active ? 'bg-amber-500/15 border-amber-400 text-amber-100' : 'bg-zinc-800 border-zinc-700 text-gray-300 hover:border-amber-500/40'}`}
                        data-testid={`sc-manual-setup-${s.id}`}>
                        <span className="mr-1">{s.icon}</span>{s.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label className="text-xs text-amber-300">Reason / notes</Label>
                <Textarea value={manualReason} onChange={e => setManualReason(e.target.value)}
                  placeholder="Why you took the trade, what happened, lesson..."
                  className="bg-zinc-800 border-amber-500/20 text-white text-sm" data-testid="sc-manual-reason" />
              </div>
              <Button onClick={submitManualTrade} disabled={submittingManual}
                className="w-full bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-amber-300 hover:to-yellow-400 text-black font-bold py-5"
                data-testid="sc-manual-submit">
                {submittingManual ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
                Import Trade
              </Button>

              {manualTrades.length > 0 && (
                <div className="space-y-2 pt-3 border-t border-zinc-700">
                  <p className="text-xs uppercase tracking-wider text-amber-300 font-semibold">Recent imports ({manualTrades.length})</p>
                  <div className="space-y-1 max-h-60 overflow-y-auto pr-1">
                    {manualTrades.map(t => (
                      <div key={t.id} className="flex items-center justify-between gap-2 bg-zinc-800/50 border border-zinc-700 rounded p-2 text-xs">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <Badge className={`text-[10px] ${t.outcome === 'win' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' : t.outcome === 'loss' ? 'bg-rose-500/20 text-rose-300 border-rose-500/40' : 'bg-zinc-600/30 text-gray-300 border-zinc-500'}`}>
                              {t.outcome?.toUpperCase()}
                            </Badge>
                            <span className="text-white font-mono">{t.instrument}</span>
                            <span className={t.pnl >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
                              {t.pnl >= 0 ? '+' : ''}{t.pnl}
                            </span>
                            <span className="text-gray-500 text-[10px]">{(t.executed_at || '').slice(0, 10)}</span>
                          </div>
                          {t.reason && <p className="text-gray-400 text-[10px] truncate mt-0.5">{t.reason}</p>}
                        </div>
                        <Button size="sm" variant="ghost" onClick={() => deleteManualTrade(t.id)}
                          className="text-rose-400 hover:bg-rose-500/10 h-6 w-6 p-0" data-testid={`sc-manual-del-${t.id}`}>
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* History */}
        <Card className="bg-zinc-900/70 border border-amber-500/15">
          <CardHeader className="cursor-pointer" onClick={() => setShowHistory(!showHistory)}>
            <CardTitle className="text-white text-lg flex items-center justify-between">
              <span className="flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-400" /> Session History ({history.length})</span>
              {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </CardTitle>
          </CardHeader>
          {showHistory && (
            <CardContent className="space-y-2 max-h-[400px] overflow-y-auto">
              {history.length === 0 && <p className="text-sm text-gray-500 italic">No past sessions yet.</p>}
              {history.map(s => {
                const stats = s.stats || {};
                const cancelled = s.status === 'cancelled';
                return (
                  <div key={s.id} className={`border rounded p-3 text-xs ${cancelled ? 'bg-amber-500/5 border-amber-500/20' : 'bg-zinc-800/50 border-zinc-700'}`}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 text-[10px]">{(s.trading_style || '').toUpperCase()}</Badge>
                        {cancelled && <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/40 text-[10px]">CANCELLED</Badge>}
                        <span className="text-white">{new Date(s.started_at).toLocaleString()}</span>
                      </div>
                      <a href={`${API}/scorecard/session/${s.id}/print?token=${token}`} target="_blank" rel="noreferrer"
                        className="text-amber-300 hover:underline flex items-center gap-1 text-[11px]" data-testid={`sc-pdf-${s.id}`}>
                        <FileDown className="w-3 h-3" /> PDF
                      </a>
                    </div>
                    <div className="text-gray-400">
                      {stats.trades_count || 0} trades · {stats.wins || 0}W/{stats.losses || 0}L · Win {stats.win_rate || 0}% ·
                      <span className={(stats.total_pnl || 0) >= 0 ? ' text-emerald-400' : ' text-rose-400'}> PnL {(stats.total_pnl || 0) >= 0 ? '+' : ''}{stats.total_pnl || 0}</span>
                    </div>
                    {s.ai_evaluation?.headline && <p className="text-amber-200/80 mt-1 text-[11px]">🤖 {s.ai_evaluation.headline}</p>}
                    {cancelled && s.ai_verdict?.verdict && <p className="text-amber-200/80 mt-1 text-[11px]">🤖 {s.ai_verdict.verdict}</p>}
                    {cancelled && s.cancel_reason && <p className="text-gray-500 mt-1 text-[10px] italic">Reason: {s.cancel_reason}</p>}
                  </div>
                );
              })}
            </CardContent>
          )}
        </Card>
      </main>

      {/* End Session Confirm Modal */}
      {confirmEnd && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => !endingSession && setConfirmEnd(false)}>
          <div className="bg-zinc-900 border border-amber-500/40 rounded-2xl max-w-md w-full p-6 space-y-4" onClick={e => e.stopPropagation()} data-testid="sc-confirm-end-modal">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/15 flex items-center justify-center">
                <ShieldAlert className="w-5 h-5 text-amber-300" />
              </div>
              <h2 className="text-lg font-bold text-white">End this live session?</h2>
            </div>
            <p className="text-sm text-gray-400">
              Your AI Coach will evaluate every trade, discipline score and emotional pattern. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setConfirmEnd(false)} disabled={endingSession} className="text-gray-400" data-testid="sc-confirm-end-cancel">
                Keep going
              </Button>
              <Button onClick={_doEndSession} disabled={endingSession}
                className="bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-400 hover:to-rose-500 text-white font-bold"
                data-testid="sc-confirm-end-yes">
                {endingSession ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Square className="w-4 h-4 mr-2" />}
                Yes, end & evaluate
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Session Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => !cancelling && setShowCancelModal(false)}>
          <div className="bg-zinc-900 border border-amber-500/40 rounded-2xl max-w-md w-full p-6 space-y-4" onClick={e => e.stopPropagation()} data-testid="sc-cancel-modal">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/15 flex items-center justify-center">
                <ShieldAlert className="w-5 h-5 text-amber-300" />
              </div>
              <h2 className="text-lg font-bold text-white">Cancel this session?</h2>
            </div>
            <p className="text-sm text-gray-400">
              The AI Coach needs context — tell it why you are walking away. This honesty is what powers smarter advice next time.
            </p>
            <div>
              <Label className="text-xs text-amber-300 block mb-1">Why are you ending early? <span className="text-rose-400">*</span></Label>
              <select value={cancelReason} onChange={e => setCancelReason(e.target.value)}
                className="w-full bg-zinc-800 border border-amber-500/20 text-white rounded-md px-3 py-2 text-sm mb-2"
                data-testid="sc-cancel-reason-select">
                <option value="">— Select reason —</option>
                <option value="No setup forming">📊 No A+ setup forming</option>
                <option value="Hit daily loss limit">🛑 Hit daily loss limit</option>
                <option value="Hit daily target">🎯 Hit daily profit target</option>
                <option value="Tilt / emotional">😤 Feeling tilted / emotional</option>
                <option value="Tired / unfocused">😴 Tired / unfocused</option>
                <option value="Market too choppy">🌊 Market too choppy / no edge</option>
                <option value="Personal commitment">📅 Personal commitment</option>
                <option value="Other">Other</option>
              </select>
              <Textarea
                value={cancelReason && !['','No setup forming','Hit daily loss limit','Hit daily target','Tilt / emotional','Tired / unfocused','Market too choppy','Personal commitment','Other'].includes(cancelReason) ? cancelReason : ''}
                onChange={e => setCancelReason(e.target.value)}
                placeholder="Or type your own reason..."
                className="bg-zinc-800 border-amber-500/20 text-white text-sm" data-testid="sc-cancel-reason-text" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowCancelModal(false)} disabled={cancelling} className="text-gray-400" data-testid="sc-cancel-modal-back">
                Back
              </Button>
              <Button onClick={submitCancelSession} disabled={cancelling || !cancelReason.trim()}
                className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-bold"
                data-testid="sc-cancel-modal-submit">
                {cancelling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <X className="w-4 h-4 mr-2" />}
                Cancel & Get Verdict
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Per-trade AI Verdict Modal */}
      {tradeVerdict && (
        <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" onClick={() => setTradeVerdict(null)}>
          <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 border-2 border-amber-400/50 rounded-2xl max-w-md w-full p-6 space-y-4 shadow-[0_0_50px_rgba(251,191,36,0.2)]" onClick={e => e.stopPropagation()} data-testid="sc-trade-verdict-modal">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-black" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[2px] text-amber-300 font-bold">AI Coach · Instant Verdict</p>
                <h2 className="text-white font-bold text-lg">
                  {tradeVerdict.trade?.outcome === 'win' ? '🎉 Win logged' :
                    tradeVerdict.trade?.outcome === 'loss' ? '😔 Loss logged' : '😐 Breakeven logged'}
                </h2>
              </div>
            </div>
            <div className="bg-black/40 border border-amber-400/20 rounded-lg p-4">
              <p className="text-[10px] text-amber-300 uppercase tracking-wider font-semibold mb-1">Why this happened</p>
              <p className="text-white text-sm leading-relaxed">{tradeVerdict.verdict}</p>
            </div>
            {tradeVerdict.insight && (
              <div className="bg-emerald-500/10 border border-emerald-400/30 rounded-lg p-4">
                <p className="text-[10px] text-emerald-300 uppercase tracking-wider font-semibold mb-1">Insight for next trade</p>
                <p className="text-white text-sm leading-relaxed">{tradeVerdict.insight}</p>
              </div>
            )}
            <Button onClick={() => setTradeVerdict(null)}
              className="w-full bg-gradient-to-r from-amber-400 to-yellow-500 text-black font-bold py-5"
              data-testid="sc-trade-verdict-close">
              Got it, back to trading
            </Button>
          </div>
        </div>
      )}

      {/* Execute Trade Modal */}
      {showExecModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => !submittingTrade && setShowExecModal(false)}>
          <div className="bg-zinc-900 border border-amber-500/40 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="sc-exec-modal">
            <div className="sticky top-0 bg-zinc-900 border-b border-amber-500/20 p-4 flex justify-between items-center">
              <h2 className="text-lg font-bold text-amber-300 flex items-center gap-2"><Zap className="w-5 h-5" /> Log Trade Execution</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowExecModal(false)} className="text-gray-400"><X className="w-4 h-4" /></Button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <Label className="text-xs text-amber-300">Instrument (optional)</Label>
                <Input value={execInstrument} onChange={e => setExecInstrument(e.target.value)}
                  placeholder="EURUSD, BTCUSD, EUR/USD OTC..."
                  className="bg-zinc-800 border-amber-500/20 text-white" data-testid="sc-exec-instrument" />
              </div>
              <div>
                <Label className="text-xs text-amber-300 block mb-2">Setup tags (select all that apply)</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {SETUP_OPTIONS.map(opt => {
                    const active = execSetups.includes(opt.id);
                    return (
                      <button key={opt.id}
                        onClick={() => setExecSetups(active ? execSetups.filter(x => x !== opt.id) : [...execSetups, opt.id])}
                        className={`text-left px-3 py-2 rounded-lg border text-sm transition-all ${active ? 'bg-amber-500/20 border-amber-400 text-amber-200' : 'bg-zinc-800 border-zinc-700 text-gray-400 hover:border-amber-500/40'}`}
                        data-testid={`sc-setup-${opt.id}`}>
                        <span className="mr-2">{opt.icon}</span> {opt.label}
                      </button>
                    );
                  })}
                </div>
                {execSetups.length === 0 && <p className="text-[11px] text-rose-300 mt-1">⚠️ No setup tag will flag this as impulsive in discipline score.</p>}
              </div>
              <div>
                <Label className="text-xs text-amber-300 block mb-2">How does your body feel right now?</Label>
                <div className="grid grid-cols-5 gap-1.5">
                  {PHYSICAL_STATES.map(ps => {
                    const active = execPhysical === ps.id;
                    return (
                      <button
                        key={ps.id}
                        type="button"
                        onClick={() => setExecPhysical(ps.id)}
                        className={`p-2 rounded-lg border-2 transition-all text-center ${active ? `bg-gradient-to-br ${ps.color} border-white/30 text-white shadow-lg` : 'border-zinc-700 bg-zinc-800 text-gray-400 hover:border-amber-500/40'}`}
                        data-testid={`sc-physical-${ps.id}`}
                      >
                        <div className="text-xl">{ps.icon}</div>
                        <div className="text-[10px] mt-0.5 font-semibold">{ps.label}</div>
                      </button>
                    );
                  })}
                </div>
                {(execPhysical === 'stressed' || execPhysical === 'heart_racing' || execPhysical === 'tired') && (
                  <p className="text-[11px] text-rose-300 mt-1">
                    ⚠️ Your body is signalling a state where mistakes are likely. Consider waiting.
                  </p>
                )}
              </div>
              <div>
                <Label className="text-xs text-amber-300">Emotion: {execEmotion}/10 <span className="text-gray-500">({execEmotion <= 3 ? 'calm' : execEmotion <= 6 ? 'focused' : 'anxious'})</span></Label>
                <Slider min={1} max={10} step={1} value={[execEmotion]} onValueChange={v => setExecEmotion(v[0])} className="mt-2" data-testid="sc-emotion-slider" />
              </div>
              <div>
                <Label className="text-xs text-amber-300">Impulse: {execImpulse}/10 <span className="text-gray-500">({execImpulse <= 3 ? 'planned' : execImpulse <= 6 ? 'moderate' : 'impulsive'})</span></Label>
                <Slider min={1} max={10} step={1} value={[execImpulse]} onValueChange={v => setExecImpulse(v[0])} className="mt-2" data-testid="sc-impulse-slider" />
              </div>
              <div>
                <Label className="text-xs text-amber-300">Pre-trade notes (optional)</Label>
                <Textarea value={execNotes} onChange={e => setExecNotes(e.target.value)}
                  placeholder="Why this trade? Entry / SL / TP plan..."
                  className="bg-zinc-800 border-amber-500/20 text-white" data-testid="sc-exec-notes" />
              </div>
              <Button onClick={submitExecuteTrade} disabled={submittingTrade}
                className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-white font-bold py-5"
                data-testid="sc-exec-submit">
                {submittingTrade ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Log Trade Executed'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Close Trade Modal */}
      {closeModalTrade && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => !submittingClose && setCloseModalTrade(null)}>
          <div className="bg-zinc-900 border border-rose-500/40 rounded-2xl max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="sc-close-modal">
            <div className="border-b border-rose-500/20 p-4 flex justify-between items-center">
              <h2 className="text-lg font-bold text-rose-300 flex items-center gap-2"><Target className="w-5 h-5" /> Close Trade</h2>
              <Button variant="ghost" size="sm" onClick={() => setCloseModalTrade(null)} className="text-gray-400"><X className="w-4 h-4" /></Button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-3 gap-2">
                {['win', 'breakeven', 'loss'].map(o => (
                  <button key={o} onClick={() => setCloseOutcome(o)}
                    className={`py-3 rounded-lg border-2 font-bold text-sm transition-all ${closeOutcome === o ? (o === 'win' ? 'bg-emerald-500 text-white border-emerald-400' : o === 'loss' ? 'bg-rose-500 text-white border-rose-400' : 'bg-zinc-600 text-white border-zinc-500') : 'bg-zinc-800 border-zinc-700 text-gray-400 hover:border-amber-500/40'}`}
                    data-testid={`sc-outcome-${o}`}>
                    {o === 'win' ? '🎉 WIN' : o === 'loss' ? '😔 LOSS' : '😐 BE'}
                  </button>
                ))}
              </div>
              <div>
                <Label className="text-xs text-amber-300">PnL amount ($ or %)</Label>
                <Input type="number" step="0.01" value={closePnl} onChange={e => setClosePnl(e.target.value)}
                  placeholder={closeOutcome === 'loss' ? '-25.50' : '42.50'}
                  className="bg-zinc-800 border-amber-500/20 text-white text-lg font-mono" data-testid="sc-close-pnl" />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                <input type="checkbox" checked={closePartial} onChange={e => setClosePartial(e.target.checked)} className="rounded" data-testid="sc-close-partial" />
                Took partial profit
              </label>
              <div>
                <Label className="text-xs text-amber-300">Why did you close this trade?</Label>
                <select value={closeReason} onChange={e => setCloseReason(e.target.value)}
                  className="w-full bg-zinc-800 border border-amber-500/20 text-white rounded-md px-3 py-2 text-sm"
                  data-testid="sc-close-reason">
                  <option value="">— Select reason —</option>
                  <option value="TP hit">🎯 Take Profit hit</option>
                  <option value="SL hit">🛑 Stop Loss hit</option>
                  <option value="Manual TP">✋ Manual TP (planned exit)</option>
                  <option value="Manual SL">⚠️ Manual SL (saw invalidation)</option>
                  <option value="Trailing stop">🪜 Trailing stop hit</option>
                  <option value="Time stop">⏱ Time stop / session end</option>
                  <option value="FOMO exit">😵 FOMO / panic exit</option>
                  <option value="News / spike">📰 News spike</option>
                  <option value="Other">Other</option>
                </select>
                {closeReason === 'FOMO exit' && <p className="text-[11px] text-rose-300 mt-1">⚠️ Flagged for AI Coach review</p>}
              </div>
              <div>
                <Label className="text-xs text-amber-300">Post-trade notes (optional)</Label>
                <Textarea value={closeNotes} onChange={e => setCloseNotes(e.target.value)}
                  placeholder="What worked / what didn't..."
                  className="bg-zinc-800 border-amber-500/20 text-white" data-testid="sc-close-notes" />
              </div>
              <Button onClick={submitCloseTrade} disabled={submittingClose}
                className="w-full bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-amber-300 hover:to-yellow-400 text-black font-bold py-5"
                data-testid="sc-close-submit">
                {submittingClose ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Close'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Session End / AI Eval Modal */}
      {endModal && (
        <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" onClick={() => setEndModal(null)}>
          <div className="bg-gradient-to-br from-zinc-900 to-black border-2 border-amber-400 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-[0_0_50px_rgba(251,191,36,0.3)]" onClick={e => e.stopPropagation()} data-testid="sc-end-modal">
            <div className="p-6">
              <div className="text-center mb-4">
                <Sparkles className="w-10 h-10 text-amber-400 mx-auto" />
                <h2 className="text-2xl font-bold bg-gradient-to-r from-amber-300 to-yellow-400 bg-clip-text text-transparent mt-2">
                  {endModal.cancelled ? 'Session Cancelled' : 'Session Complete'}
                </h2>
                <p className="text-xs text-amber-200/60 tracking-[2px] uppercase">AI Coach Evaluation</p>
              </div>

              {endModal.cancelled && endModal.verdict && (
                <div className="space-y-3 mb-4">
                  <div className="bg-amber-500/10 border border-amber-400/40 rounded-lg p-4">
                    <p className="text-[10px] text-amber-300 uppercase tracking-wider font-semibold mb-1">Coach's verdict on cancelling</p>
                    <p className="text-white text-sm">{endModal.verdict.verdict}</p>
                    {endModal.verdict.insight && (
                      <p className="text-amber-200/80 text-xs mt-2 italic">💡 {endModal.verdict.insight}</p>
                    )}
                  </div>
                  {endModal.session?.cancel_reason && (
                    <p className="text-gray-500 text-xs text-center italic">Your reason: "{endModal.session.cancel_reason}"</p>
                  )}
                </div>
              )}

              {!endModal.cancelled && endModal.session?.ai_evaluation?.error ? (
                <p className="text-rose-300 text-sm">AI evaluation unavailable: {endModal.session.ai_evaluation.error}</p>
              ) : !endModal.cancelled && endModal.session?.ai_evaluation ? (
                <div className="space-y-3">
                  <p className="text-center text-lg font-bold text-white">{endModal.session.ai_evaluation.headline}</p>
                  <div className="flex justify-center gap-4">
                    <div className="text-center">
                      <div className="text-3xl font-black text-amber-400">{endModal.session.ai_evaluation.discipline_grade}</div>
                      <div className="text-[10px] text-gray-400 uppercase tracking-wider">Discipline</div>
                    </div>
                    <div className="text-center max-w-xs">
                      <div className="text-sm text-amber-200">{endModal.session.ai_evaluation.emotional_state}</div>
                      <div className="text-[10px] text-gray-400 uppercase tracking-wider mt-1">Emotional Read</div>
                    </div>
                  </div>

                  {endModal.session.ai_evaluation.time_discipline && (
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-center">
                      <p className="text-[10px] text-blue-300 uppercase tracking-wider font-semibold">⏱ Time Discipline</p>
                      <p className="text-blue-100 text-sm mt-1">{endModal.session.ai_evaluation.time_discipline}</p>
                    </div>
                  )}

                  {endModal.session.ai_evaluation.risk_warning && (
                    <div className="bg-rose-500/10 border border-rose-500/40 rounded-lg p-3 text-rose-200 text-sm">
                      <ShieldAlert className="w-4 h-4 inline mr-1" /> {endModal.session.ai_evaluation.risk_warning}
                    </div>
                  )}

                  {endModal.session.ai_evaluation.chat_insights && (
                    <div className="bg-purple-500/10 border border-purple-400/30 rounded-lg p-3">
                      <p className="text-[10px] text-purple-300 uppercase tracking-wider font-semibold mb-1">🗣 What your chat revealed</p>
                      <p className="text-purple-100 text-sm">{endModal.session.ai_evaluation.chat_insights}</p>
                    </div>
                  )}

                  {endModal.session.ai_evaluation.prop_firm_plan && (
                    <div className="bg-emerald-500/10 border border-emerald-400/40 rounded-lg p-3">
                      <p className="text-[10px] text-emerald-300 uppercase tracking-wider font-semibold mb-1">💼 Prop-firm plan</p>
                      <p className="text-emerald-100 text-sm">{endModal.session.ai_evaluation.prop_firm_plan}</p>
                    </div>
                  )}

                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
                      <p className="text-xs font-bold text-emerald-300 uppercase tracking-wider mb-2">✅ What went well</p>
                      <ul className="text-xs text-emerald-100 space-y-1 list-disc list-inside">
                        {(endModal.session.ai_evaluation.what_went_well || []).map((x, i) => <li key={i}>{x}</li>)}
                      </ul>
                    </div>
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                      <p className="text-xs font-bold text-amber-300 uppercase tracking-wider mb-2">⚠️ Improve</p>
                      <ul className="text-xs text-amber-100 space-y-1 list-disc list-inside">
                        {(endModal.session.ai_evaluation.what_to_improve || []).map((x, i) => <li key={i}>{x}</li>)}
                      </ul>
                    </div>
                  </div>

                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                    <p className="text-xs font-bold text-blue-300 uppercase tracking-wider mb-2">🎯 Plan for next session</p>
                    <ul className="text-xs text-blue-100 space-y-1 list-disc list-inside">
                      {(endModal.session.ai_evaluation.next_session_plan || []).map((x, i) => <li key={i}>{x}</li>)}
                    </ul>
                  </div>

                  <div className="text-center text-xs text-gray-400 pt-2">
                    Best setup: <span className="text-emerald-300 font-bold">{endModal.session.ai_evaluation.best_setup_today}</span> ·
                    Avoid: <span className="text-rose-300 font-bold">{endModal.session.ai_evaluation.worst_setup_today}</span>
                  </div>
                </div>
              ) : (
                <p className="text-gray-400 text-center">No trades to evaluate.</p>
              )}

              <div className="flex flex-col sm:flex-row gap-2 mt-6 pt-4 border-t border-zinc-800">
                <a href={`${API}/scorecard/session/${endModal.session.id}/print?token=${token}`} target="_blank" rel="noreferrer"
                  className="flex-1">
                  <Button variant="outline" className="w-full border-amber-400/40 text-amber-300 hover:bg-amber-500/10" data-testid="sc-download-pdf-btn">
                    <FileDown className="w-4 h-4 mr-2" /> Download PDF Scorecard
                  </Button>
                </a>
                <Button onClick={() => setEndModal(null)}
                  className="flex-1 bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-amber-300 hover:to-yellow-400 text-black font-bold"
                  data-testid="sc-end-modal-close-btn">
                  Done
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== Account details modal (per-account PnL & trade history) ===== */}
      {detailAccount && (
        <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" onClick={() => setDetailAccount(null)}>
          <div className="bg-zinc-900 border-2 border-amber-400/40 rounded-2xl max-w-3xl w-full p-5 space-y-4 max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="sc-acct-detail-modal">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[2px] text-amber-300 font-bold">
                  {(detailAccount.account_class || 'funded') === 'eval' ? '🎓 Eval account' : '💼 Funded account'} · details
                </p>
                <h2 className="text-white font-bold text-xl truncate">{detailAccount.name}</h2>
                <p className="text-xs text-gray-400">
                  {detailAccount.firm && <span>{detailAccount.firm} · </span>}
                  {detailAccount.account_size && <span>{detailAccount.account_size}</span>}
                </p>
              </div>
              <button onClick={() => setDetailAccount(null)} className="text-gray-400 hover:text-white" data-testid="sc-acct-detail-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {detailLoading && (
              <div className="text-center py-8 text-gray-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</div>
            )}

            {detailData && (() => {
              const acc = detailData.account;
              const agg = detailData.aggregate || {};
              const targetHit = acc.profit_target > 0 && (acc.current_pnl ?? 0) >= acc.profit_target;
              const ddBreached = acc.max_drawdown > 0 && Math.abs(Math.min(0, acc.current_pnl ?? 0)) >= acc.max_drawdown;
              return (
                <>
                  {/* Status banner */}
                  {targetHit && acc.status === 'active' && (acc.account_class || 'funded') === 'eval' && (
                    <div className="bg-emerald-500/15 border-2 border-emerald-400 rounded-lg p-4 flex items-center justify-between gap-3" data-testid="sc-acct-target-banner">
                      <div>
                        <p className="text-emerald-300 font-bold text-base">🎯 Eval objective reached!</p>
                        <p className="text-emerald-100 text-xs">Promote this account to Funded to start a fresh PnL run.</p>
                      </div>
                      <Button onClick={() => { promoteEvalToFunded(acc.id); setDetailAccount(null); }}
                        className="bg-gradient-to-r from-emerald-400 to-yellow-400 text-black font-bold"
                        data-testid="sc-acct-promote-from-detail">
                        🎉 Promote
                      </Button>
                    </div>
                  )}
                  {acc.status === 'passed' && (
                    <div className="bg-emerald-500/15 border-2 border-emerald-400 rounded-lg p-3">
                      <p className="text-emerald-300 font-bold">✅ {(acc.account_class || 'funded') === 'eval' ? 'Eval passed' : 'Account passed'}</p>
                    </div>
                  )}
                  {acc.status === 'blown' && (
                    <div className="bg-rose-500/15 border-2 border-rose-400 rounded-lg p-3">
                      <p className="text-rose-300 font-bold">❌ Account blown — max drawdown breached.</p>
                    </div>
                  )}
                  {ddBreached && acc.status === 'active' && (
                    <div className="bg-rose-500/15 border-2 border-rose-400 rounded-lg p-3">
                      <p className="text-rose-300 font-bold">⚠️ Max drawdown breached. Mark blown or reset.</p>
                    </div>
                  )}

                  {/* Stats grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <StatBox label="Cumulative PnL" value={`${(acc.current_pnl ?? 0) >= 0 ? '+' : ''}$${(acc.current_pnl ?? 0).toFixed(2)}`} accent={(acc.current_pnl ?? 0) >= 0 ? 'emerald' : 'rose'} />
                    <StatBox label="Trades" value={agg.trades_count || 0} />
                    <StatBox label="Win rate" value={`${agg.win_rate || 0}%`} accent={(agg.win_rate || 0) >= 50 ? 'emerald' : 'rose'} />
                    <StatBox label="Sessions" value={agg.sessions_count || 0} />
                  </div>

                  {/* Progress to target */}
                  {acc.profit_target > 0 && (
                    <div>
                      <div className="flex items-center justify-between text-[11px] mb-1">
                        <span className="text-gray-400">Path to target</span>
                        <span className={`font-mono font-bold ${targetHit ? 'text-emerald-300' : 'text-amber-300'}`}>
                          ${Math.max(0, acc.current_pnl ?? 0).toFixed(2)} / ${acc.profit_target.toFixed(2)} ({acc.pct_to_target}%)
                        </span>
                      </div>
                      <div className="h-3 rounded-full bg-zinc-800 overflow-hidden border border-zinc-700">
                        <div className={`h-full ${targetHit ? 'bg-emerald-400' : 'bg-gradient-to-r from-amber-400 to-emerald-400'}`} style={{ width: `${Math.min(100, acc.pct_to_target)}%` }}></div>
                      </div>
                    </div>
                  )}
                  {acc.max_drawdown > 0 && (
                    <div>
                      <div className="flex items-center justify-between text-[11px] mb-1">
                        <span className="text-gray-400">Drawdown used</span>
                        <span className={`font-mono font-bold ${acc.pct_dd_used >= 75 ? 'text-rose-300' : 'text-gray-300'}`}>
                          ${Math.abs(Math.min(0, acc.current_pnl ?? 0)).toFixed(2)} / ${acc.max_drawdown.toFixed(2)} ({acc.pct_dd_used}%)
                        </span>
                      </div>
                      <div className="h-2.5 rounded-full bg-zinc-800 overflow-hidden">
                        <div className={`h-full ${acc.pct_dd_used >= 75 ? 'bg-rose-500' : 'bg-amber-400'}`} style={{ width: `${acc.pct_dd_used}%` }}></div>
                      </div>
                    </div>
                  )}

                  {/* Sessions list */}
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-amber-300 mb-2">Sessions ({detailData.sessions.length})</p>
                    {detailData.sessions.length === 0 ? (
                      <p className="text-[11px] text-gray-500 italic">No sessions linked to this account yet.</p>
                    ) : (
                      <div className="space-y-1.5 max-h-44 overflow-y-auto pr-1">
                        {detailData.sessions.map(s => {
                          const stPnl = (s.stats && s.stats.total_pnl) || 0;
                          return (
                            <div key={s.id} className="bg-zinc-800/50 border border-zinc-700 rounded p-2 text-[11px]">
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-white">{new Date(s.started_at).toLocaleString()}</span>
                                <span className={stPnl >= 0 ? 'text-emerald-300 font-mono' : 'text-rose-300 font-mono'}>
                                  {stPnl >= 0 ? '+' : ''}${stPnl.toFixed(2)}
                                </span>
                              </div>
                              <p className="text-gray-500 text-[10px]">
                                {(s.trading_style || '').toUpperCase()} · {(s.stats?.trades_count || 0)} trades · {s.status || 'active'}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Trade history */}
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-amber-300 mb-2">Trade history ({detailData.trades.length})</p>
                    {detailData.trades.length === 0 ? (
                      <p className="text-[11px] text-gray-500 italic">No trades for this account yet.</p>
                    ) : (
                      <div className="space-y-1 max-h-60 overflow-y-auto pr-1">
                        {detailData.trades.map(t => (
                          <div key={t.id} className="bg-zinc-800/40 border border-zinc-700 rounded p-2 text-[11px] flex items-center justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge className={`text-[9px] ${t.outcome === 'win' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' : t.outcome === 'loss' ? 'bg-rose-500/20 text-rose-300 border-rose-500/40' : 'bg-zinc-600/30 text-gray-300 border-zinc-500'}`}>
                                  {(t.outcome || 'open').toUpperCase()}
                                </Badge>
                                {t.instrument && <span className="text-white font-mono">{t.instrument}</span>}
                                <span className={t.pnl >= 0 ? 'text-emerald-300 font-mono' : 'text-rose-300 font-mono'}>
                                  {t.pnl >= 0 ? '+' : ''}${(t.pnl || 0).toFixed(2)}
                                </span>
                                <span className="text-gray-500 text-[9px]">{t.executed_at && new Date(t.executed_at).toLocaleDateString()}</span>
                              </div>
                              {(t.setup_tags || []).length > 0 && (
                                <p className="text-gray-400 text-[10px] truncate">{(t.setup_tags || []).join(', ')}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* ===== Funded account add/edit modal ===== */}
      {showFundedAccountModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => !faSubmitting && setShowFundedAccountModal(false)}>
          <div className={`bg-zinc-900 border ${faForm.account_class === 'eval' ? 'border-amber-500/40' : 'border-emerald-500/40'} rounded-2xl max-w-md w-full p-5 space-y-3 max-h-[90vh] overflow-y-auto`} onClick={e => e.stopPropagation()} data-testid="sc-funded-modal">
            <h2 className="text-white font-bold text-base flex items-center gap-2">
              {faForm.account_class === 'eval' ? '🎓' : '💼'} {editingFundedAccount ? `Edit ${faForm.account_class === 'eval' ? 'eval' : 'funded'} account` : `Save new ${faForm.account_class === 'eval' ? 'eval' : 'funded'} account`}
            </h2>
            <p className="text-xs text-gray-400">
              {faForm.account_class === 'eval'
                ? 'Track each eval (challenge) separately. When you hit the profit target, promote it to a new Funded account in one click — PnL resets and the details copy over.'
                : 'Save each prop-firm funded account separately so the coach can track cumulative PnL, payouts, and drawdown headroom per account.'}
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-2">
                <Label className="text-xs text-amber-300">Account name <span className="text-rose-400">*</span></Label>
                <Input value={faForm.name} onChange={e => setFaForm({ ...faForm, name: e.target.value })}
                  placeholder={faForm.account_class === 'eval' ? 'FTMO 50K Eval #1' : 'FTMO 50K Funded'}
                  className="bg-zinc-800 border-zinc-700 text-white" data-testid="sc-fa-name" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-amber-300">Account type</Label>
                <select value={faForm.account_class} onChange={e => setFaForm({ ...faForm, account_class: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-md px-3 py-2 text-sm"
                  data-testid="sc-fa-class">
                  <option value="eval">🎓 Eval (challenge)</option>
                  <option value="funded">💼 Funded (real)</option>
                </select>
              </div>
              <div>
                <Label className="text-xs text-amber-300">Prop firm</Label>
                <Input value={faForm.firm} onChange={e => setFaForm({ ...faForm, firm: e.target.value })}
                  placeholder="FTMO / Topstep" className="bg-zinc-800 border-emerald-500/20 text-white" data-testid="sc-fa-firm" />
              </div>
              <div>
                <Label className="text-xs text-amber-300">Account size</Label>
                <Input value={faForm.account_size} onChange={e => setFaForm({ ...faForm, account_size: e.target.value })}
                  placeholder="$50K, $100K…" className="bg-zinc-800 border-emerald-500/20 text-white" data-testid="sc-fa-size" />
              </div>
              <div>
                <Label className="text-xs text-amber-300">Profit target ($)</Label>
                <Input type="number" step="0.01" value={faForm.profit_target} onChange={e => setFaForm({ ...faForm, profit_target: e.target.value })}
                  placeholder="2000" className="bg-zinc-800 border-emerald-500/20 text-white font-mono" data-testid="sc-fa-target" />
              </div>
              <div>
                <Label className="text-xs text-amber-300">Max drawdown ($)</Label>
                <Input type="number" step="0.01" value={faForm.max_drawdown} onChange={e => setFaForm({ ...faForm, max_drawdown: e.target.value })}
                  placeholder="2500" className="bg-zinc-800 border-emerald-500/20 text-white font-mono" data-testid="sc-fa-maxdd" />
              </div>
              <div>
                <Label className="text-xs text-amber-300">Starting balance ($)</Label>
                <Input type="number" step="0.01" value={faForm.starting_balance} onChange={e => setFaForm({ ...faForm, starting_balance: e.target.value })}
                  placeholder="50000 (optional)" className="bg-zinc-800 border-emerald-500/20 text-white font-mono" data-testid="sc-fa-start-bal" />
              </div>
              <div>
                <Label className="text-xs text-amber-300">Current PnL ($)</Label>
                <Input type="number" step="0.01" value={faForm.current_pnl} onChange={e => setFaForm({ ...faForm, current_pnl: e.target.value })}
                  placeholder="0 (will auto-update)" className="bg-zinc-800 border-emerald-500/20 text-white font-mono" data-testid="sc-fa-current-pnl" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-amber-300">Status</Label>
              <select value={faForm.status} onChange={e => setFaForm({ ...faForm, status: e.target.value })}
                className="w-full bg-zinc-800 border border-emerald-500/20 text-white rounded-md px-3 py-2 text-sm"
                data-testid="sc-fa-status">
                <option value="active">Active</option>
                <option value="passed">Passed (eval cleared)</option>
                <option value="blown">Blown</option>
                <option value="retired">Retired</option>
              </select>
            </div>
            <div>
              <Label className="text-xs text-amber-300">Notes</Label>
              <Textarea value={faForm.notes} onChange={e => setFaForm({ ...faForm, notes: e.target.value })}
                placeholder="Daily DD, payout cycle, rules…"
                className="bg-zinc-800 border-emerald-500/20 text-white text-sm" rows={2}
                data-testid="sc-fa-notes" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowFundedAccountModal(false)} disabled={faSubmitting} className="text-gray-400" data-testid="sc-fa-cancel">
                Cancel
              </Button>
              <Button onClick={submitFundedAccount} disabled={faSubmitting || !faForm.name.trim()}
                className="bg-gradient-to-r from-emerald-400 to-emerald-500 hover:from-emerald-300 hover:to-emerald-400 text-black font-bold"
                data-testid="sc-fa-submit">
                {faSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {editingFundedAccount ? 'Save changes' : 'Save account'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Account-profile modals: Expense / Payout / Blown ===== */}
      {(showAddExpense || showAddPayout || showAddBlown) && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => { if (!profSubmitting) { setShowAddExpense(false); setShowAddPayout(false); setShowAddBlown(false); } }}
        >
          <div className="bg-zinc-900 border border-amber-500/40 rounded-2xl max-w-md w-full p-5 space-y-3" onClick={e => e.stopPropagation()} data-testid="sc-profile-modal">
            <h2 className="text-white font-bold text-base flex items-center gap-2">
              {showAddExpense && <>💸 Add prop-firm expense</>}
              {showAddPayout && <>💰 Record payout</>}
              {showAddBlown && <>🪦 Mark account blown</>}
            </h2>
            <p className="text-xs text-gray-400">
              {showAddExpense && 'Track every dollar paid to prop firms (eval purchases, resets, addons).'}
              {showAddPayout && 'Log every payout you actually received. This grows your overall PnL profile.'}
              {showAddBlown && 'Record blown evals/funded so the coach factors survival rate into recommendations.'}
            </p>

            {!showAddBlown && (
              <div>
                <Label className="text-xs text-amber-300">Amount ($)</Label>
                <Input type="number" step="0.01" value={profForm.amount}
                  onChange={e => setProfForm({ ...profForm, amount: e.target.value })}
                  placeholder="500" className="bg-zinc-800 border-amber-500/20 text-white font-mono"
                  data-testid="sc-prof-amount" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-amber-300">Prop firm</Label>
                <Input value={profForm.firm} onChange={e => setProfForm({ ...profForm, firm: e.target.value })}
                  placeholder="FTMO / Topstep…" className="bg-zinc-800 border-amber-500/20 text-white"
                  data-testid="sc-prof-firm" />
              </div>
              <div>
                <Label className="text-xs text-amber-300">Account size</Label>
                <Input value={profForm.account_size} onChange={e => setProfForm({ ...profForm, account_size: e.target.value })}
                  placeholder="$50K, $100K…" className="bg-zinc-800 border-amber-500/20 text-white"
                  data-testid="sc-prof-size" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-amber-300">Date</Label>
                <Input type="date" value={profForm.date} onChange={e => setProfForm({ ...profForm, date: e.target.value })}
                  className="bg-zinc-800 border-amber-500/20 text-white" data-testid="sc-prof-date" />
              </div>
              {showAddExpense && (
                <div>
                  <Label className="text-xs text-amber-300">Type</Label>
                  <select value={profForm.type} onChange={e => setProfForm({ ...profForm, type: e.target.value })}
                    className="w-full bg-zinc-800 border border-amber-500/20 text-white rounded-md px-3 py-2 text-sm"
                    data-testid="sc-prof-type">
                    <option value="eval_purchase">Eval purchase</option>
                    <option value="reset">Reset</option>
                    <option value="addon">Add-on</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              )}
              {showAddBlown && (
                <div>
                  <Label className="text-xs text-amber-300">Account type</Label>
                  <select value={profForm.account_type} onChange={e => setProfForm({ ...profForm, account_type: e.target.value })}
                    className="w-full bg-zinc-800 border border-amber-500/20 text-white rounded-md px-3 py-2 text-sm"
                    data-testid="sc-prof-blown-type">
                    <option value="eval">Eval</option>
                    <option value="funded">Funded</option>
                  </select>
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs text-amber-300">Notes (optional)</Label>
              <Textarea value={profForm.notes} onChange={e => setProfForm({ ...profForm, notes: e.target.value })}
                placeholder={showAddBlown ? 'What triggered the bust? Daily DD? Max DD?' : 'Reference / promo code / payout cycle…'}
                className="bg-zinc-800 border-amber-500/20 text-white text-sm" rows={2}
                data-testid="sc-prof-notes" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => { setShowAddExpense(false); setShowAddPayout(false); setShowAddBlown(false); }}
                disabled={profSubmitting} className="text-gray-400" data-testid="sc-prof-cancel">
                Cancel
              </Button>
              <Button
                onClick={() => submitProfileEntry(showAddExpense ? 'expense' : showAddPayout ? 'payout' : 'blown')}
                disabled={profSubmitting}
                className={`text-black font-bold ${showAddExpense ? 'bg-rose-400 hover:bg-rose-300' : showAddPayout ? 'bg-emerald-400 hover:bg-emerald-300' : 'bg-amber-400 hover:bg-amber-300'}`}
                data-testid="sc-prof-submit">
                {profSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {showAddExpense && 'Add expense'}
                {showAddPayout && 'Record payout'}
                {showAddBlown && 'Mark blown'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Floating AI Coach Chat Bubble (only when session active) ===== */}
      {session && (
        <>
          {!chatOpen && (
            <button
              onClick={() => setChatOpen(true)}
              className="fixed bottom-6 right-6 z-40 w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 hover:from-amber-300 hover:to-yellow-400 shadow-[0_0_30px_rgba(251,191,36,0.5)] flex items-center justify-center transition-transform hover:scale-110"
              data-testid="sc-chat-bubble-open"
              title="Chat with AI Coach"
            >
              <MessageCircle className="w-7 h-7 text-black" />
              {chatUnread > 0 && (
                <span className="absolute -top-1 -right-1 bg-rose-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center border-2 border-black">
                  {chatUnread > 9 ? '9+' : chatUnread}
                </span>
              )}
            </button>
          )}

          {chatOpen && (
            <div className="fixed bottom-6 right-6 z-40 w-[380px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-3rem)] bg-zinc-900 border-2 border-amber-400/40 rounded-2xl shadow-[0_0_40px_rgba(251,191,36,0.25)] flex flex-col overflow-hidden" data-testid="sc-chat-panel">
              {/* Header */}
              <div className="px-4 py-3 bg-gradient-to-r from-amber-500/15 to-yellow-500/10 border-b border-amber-400/30 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center">
                    <Brain className="w-5 h-5 text-black" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm leading-tight">AI Session Coach</p>
                    <p className="text-[10px] text-emerald-400 leading-tight flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span> Live
                    </p>
                  </div>
                </div>
                <button onClick={() => setChatOpen(false)} className="text-gray-400 hover:text-white p-1" data-testid="sc-chat-close">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Messages */}
              <div ref={chatScrollRef} className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="sc-chat-messages">
                {chatMessages.length === 0 && !chatSending && (
                  <div className="text-center py-6">
                    <Sparkles className="w-8 h-8 text-amber-400/60 mx-auto mb-2" />
                    <p className="text-amber-200/80 text-sm font-semibold">Coach is listening</p>
                    <p className="text-gray-500 text-xs mt-1 px-4">
                      Ask anything — "Should I wait for confluence?", "I'm tempted to revenge-trade", or drop a chart screenshot.
                    </p>
                  </div>
                )}
                {chatMessages.map(m => {
                  const isUser = m.role === 'user';
                  const imgSrc = m.image_base64
                    ? (m.image_base64.startsWith('data:') ? m.image_base64 : `data:image/jpeg;base64,${m.image_base64}`)
                    : null;
                  return (
                    <div key={m.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`} data-testid={`sc-chat-msg-${m.role}`}>
                      <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${isUser ? 'bg-gradient-to-br from-amber-500 to-yellow-600 text-black font-medium rounded-br-sm' : 'bg-zinc-800 text-gray-100 border border-zinc-700 rounded-bl-sm'}`}>
                        {imgSrc && (
                          <img
                            src={imgSrc}
                            alt="Chart"
                            className="rounded-lg mb-2 max-w-full max-h-56 object-contain border border-black/20"
                          />
                        )}
                        {m.content && <div className="whitespace-pre-wrap">{m.content}</div>}
                        {!m.content && !imgSrc && m.has_image && (
                          <div className="text-[10px] opacity-70 italic">(chart screenshot)</div>
                        )}
                        <div className={`text-[9px] mt-1 ${isUser ? 'text-black/50' : 'text-gray-500'}`}>
                          {new Date(m.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {chatSending && (
                  <div className="flex justify-start">
                    <div className="bg-zinc-800 border border-zinc-700 rounded-2xl rounded-bl-sm px-3 py-2 text-sm text-amber-200/80 flex items-center gap-2">
                      <Loader2 className="w-3 h-3 animate-spin" /> coach is thinking…
                    </div>
                  </div>
                )}
              </div>

              {/* Image preview */}
              {chatImage && (
                <div className="px-3 py-2 border-t border-zinc-800 bg-zinc-900 flex items-center gap-2">
                  <img src={chatImage} alt="attachment" className="h-12 w-12 rounded object-cover border border-amber-400/30" />
                  <span className="text-xs text-amber-300 flex-1">Chart ready to send</span>
                  <button onClick={() => setChatImage(null)} className="text-gray-400 hover:text-rose-400" data-testid="sc-chat-image-clear">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Composer */}
              <div className="p-3 border-t border-zinc-800 bg-zinc-900/60">
                <div className="flex items-end gap-2">
                  <input
                    ref={chatFileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={onPickChatImage}
                    data-testid="sc-chat-image-input"
                  />
                  <button
                    onClick={() => chatFileRef.current && chatFileRef.current.click()}
                    className="p-2 rounded-lg bg-zinc-800 border border-zinc-700 text-amber-400 hover:bg-zinc-700"
                    title="Attach chart screenshot"
                    data-testid="sc-chat-image-btn"
                  >
                    <ImageIcon className="w-4 h-4" />
                  </button>
                  <Textarea
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
                    placeholder="Ask the coach..."
                    rows={1}
                    className="flex-1 min-h-[40px] max-h-24 resize-none bg-zinc-800 border-zinc-700 text-white text-sm"
                    data-testid="sc-chat-input"
                  />
                  <Button
                    onClick={sendChat}
                    disabled={chatSending || (!chatInput.trim() && !chatImage)}
                    className="bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-amber-300 hover:to-yellow-400 text-black h-10 px-3"
                    data-testid="sc-chat-send"
                  >
                    {chatSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-[10px] text-gray-500 mt-1.5 text-center">
                  Coach sees your live trades · transcript feeds into the session report
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// === Sub-components ===
const accentMap = {
  emerald: 'text-emerald-400',
  rose: 'text-rose-400',
  amber: 'text-amber-400',
  default: 'text-white'
};

function StatChip({ label, value, accent = 'default' }) {
  return (
    <div className="bg-black/40 border border-amber-500/10 rounded-lg p-2 text-center">
      <div className={`text-lg font-bold ${accentMap[accent]}`}>{value}</div>
      <div className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</div>
    </div>
  );
}

function StatBox({ label, value, accent = 'default' }) {
  return (
    <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-3 text-center">
      <div className={`text-xl font-bold ${accentMap[accent]}`}>{value}</div>
      <div className="text-[10px] text-gray-400 uppercase tracking-wider mt-1">{label}</div>
    </div>
  );
}

function AccountCard({ a, klass, onEdit, onDelete, onPromote, onView }) {
  const pnlPos = (a.current_pnl ?? 0) >= 0;
  const statusColor = a.status === 'blown' ? 'bg-rose-500/20 text-rose-300 border-rose-500/40'
    : a.status === 'passed' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
    : a.status === 'retired' ? 'bg-zinc-600/30 text-gray-300 border-zinc-500'
    : (klass === 'eval' ? 'bg-amber-500/20 text-amber-300 border-amber-500/40' : 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40');
  const borderColor = klass === 'eval' ? 'border-amber-500/20' : 'border-emerald-500/20';
  const canPromote = klass === 'eval' && a.status === 'active' && a.profit_target > 0 && (a.current_pnl ?? 0) >= a.profit_target;
  return (
    <div className={`bg-zinc-900/60 border ${borderColor} rounded-lg p-3`} data-testid={`sc-acct-${a.id}`}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <p className="text-white font-bold text-sm truncate flex items-center gap-1">
          {klass === 'eval' ? '🎓' : '💼'} {a.name}
        </p>
        <div className="flex items-center gap-1">
          <Badge className={`${statusColor} text-[9px] border`}>{(a.status || 'active').toUpperCase()}</Badge>
          {onView && (
            <button onClick={onView} className="text-gray-400 hover:text-amber-300 text-xs" title="View PnL & trade history" data-testid={`sc-acct-view-${a.id}`}>📋</button>
          )}
          <button onClick={onEdit} className="text-gray-400 hover:text-amber-300 text-xs" data-testid={`sc-acct-edit-${a.id}`}>✎</button>
          <button onClick={onDelete} className="text-gray-400 hover:text-rose-400" data-testid={`sc-acct-del-${a.id}`}>
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>
      <p className="text-[11px] text-gray-400">
        {a.firm && <span>{a.firm} · </span>}
        {a.account_size && <span>{a.account_size}</span>}
      </p>
      <div className="flex items-center justify-between mt-2">
        <div>
          <p className="text-[9px] text-gray-500 uppercase tracking-wider">Cumulative PnL</p>
          <p className={`text-base font-black font-mono ${pnlPos ? 'text-emerald-300' : 'text-rose-300'}`} data-testid={`sc-acct-pnl-${a.id}`}>
            {pnlPos ? '+' : ''}${(a.current_pnl ?? 0).toLocaleString()}
          </p>
        </div>
        <div className="text-right">
          <p className="text-[9px] text-gray-500 uppercase tracking-wider">W / L</p>
          <p className="text-xs text-gray-300 font-mono">{a.total_wins || 0} / {a.total_losses || 0}</p>
        </div>
      </div>
      {a.profit_target > 0 && (
        <div className="mt-2">
          <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden">
            <div className={`h-full ${a.pct_to_target >= 100 ? 'bg-emerald-400' : 'bg-gradient-to-r from-amber-400 to-emerald-400'}`} style={{ width: `${Math.min(100, a.pct_to_target)}%` }}></div>
          </div>
          <p className="text-[9px] text-gray-500 mt-0.5">{a.pct_to_target}% to ${a.profit_target} target</p>
        </div>
      )}
      {canPromote && (
        <button onClick={onPromote}
          className="mt-2 w-full text-[11px] py-1.5 rounded-md bg-gradient-to-r from-emerald-400 to-yellow-400 text-black font-bold hover:from-emerald-300 hover:to-yellow-300 transition"
          data-testid={`sc-acct-promote-${a.id}`}>
          🎉 Target hit — Promote to Funded
        </button>
      )}
    </div>
  );
}

function ProfileList({ title, items, kind, color, onDelete, amountKey = 'amount' }) {
  const colorMap = {
    rose: 'text-rose-300 border-rose-500/20',
    emerald: 'text-emerald-300 border-emerald-500/20',
    zinc: 'text-gray-300 border-zinc-700',
  };
  return (
    <div className={`bg-black/30 border rounded-lg p-3 ${colorMap[color] || colorMap.zinc}`}>
      <p className="text-xs font-bold uppercase tracking-wider mb-2 opacity-90">{title} ({items.length})</p>
      {items.length === 0 ? (
        <p className="text-[11px] text-gray-500 italic">None yet</p>
      ) : (
        <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
          {items.map(it => (
            <div key={it.id} className="flex items-center justify-between gap-2 bg-zinc-900/60 border border-white/5 rounded p-2 text-[11px]">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  {amountKey && (
                    <span className="font-mono font-bold text-white">${(it[amountKey] || 0).toLocaleString()}</span>
                  )}
                  {it.firm && <span className="text-amber-300/80">{it.firm}</span>}
                  {it.account_size && <span className="text-gray-500">{it.account_size}</span>}
                </div>
                <div className="text-[10px] text-gray-500">
                  {it.date}{it.type ? ' · ' + it.type.replace('_', ' ') : ''}{it.account_type ? ' · ' + it.account_type : ''}
                </div>
                {it.notes && <div className="text-[10px] text-gray-400 truncate italic mt-0.5">{it.notes}</div>}
              </div>
              <button onClick={() => onDelete(kind, it.id)} className="text-gray-500 hover:text-rose-400" data-testid={`sc-prof-del-${kind}-${it.id}`}>
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FundedScoreboard({ session, livePnl, linkedAccount }) {
  const target = parseFloat(session.funded_profit_target);
  const maxDD = parseFloat(session.funded_max_drawdown);
  const hasTarget = !isNaN(target) && target > 0;
  const hasDD = !isNaN(maxDD) && maxDD > 0;
  if (!hasTarget && !hasDD) return null;

  // Payout progress (only counts positive PnL toward target)
  const progressPct = hasTarget ? Math.min(100, Math.max(0, (livePnl / target) * 100)) : 0;
  const remaining = hasTarget ? Math.max(0, target - livePnl) : 0;
  const hitTarget = hasTarget && livePnl >= target;

  // Drawdown headroom (current loss vs max DD)
  const drawdown = livePnl < 0 ? Math.abs(livePnl) : 0;
  const ddUsedPct = hasDD ? Math.min(100, (drawdown / maxDD) * 100) : 0;
  const ddRemaining = hasDD ? Math.max(0, maxDD - drawdown) : 0;
  const ddCritical = hasDD && ddUsedPct >= 75;

  return (
    <div className="mt-4 pt-4 border-t border-amber-500/20 space-y-3" data-testid="sc-funded-scoreboard">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="text-lg">{session.account_type === 'eval' ? '🎓' : '💼'}</span>
          <div>
            <p className="text-[10px] uppercase tracking-[2px] text-emerald-300 font-bold">
              {session.account_type === 'eval' ? 'Eval Account Progress' : 'Funded Account Scoreboard'}
            </p>
            <p className="text-[11px] text-gray-400">
              {session.funded_firm || 'Prop firm'}{session.funded_account_size ? ` · ${session.funded_account_size}` : ''}
              {session.funded_account_id ? <span className="ml-1 text-emerald-400">· tracked</span> : <span className="ml-1 text-rose-400">· NOT linked</span>}
            </p>
          </div>
        </div>
        {hitTarget && (
          <span className="text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-emerald-500 text-black animate-pulse">
            🎯 Payout target hit!
          </span>
        )}
      </div>

      {linkedAccount && (
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 flex items-center justify-between" data-testid="sc-linked-account-live">
          <div className="text-[11px] text-emerald-200/80">
            <span className="font-semibold">{linkedAccount.name}</span>
            <span className="text-emerald-300/60 ml-1">· live account PnL (incl. open trades)</span>
          </div>
          <div className="text-right">
            <div className={`font-mono font-bold text-sm ${(linkedAccount.current_pnl || 0) >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
              ${(linkedAccount.current_pnl || 0).toFixed(2)}
            </div>
            <div className="text-[9px] uppercase tracking-wider text-gray-500">
              {(linkedAccount.total_wins || 0)}W / {(linkedAccount.total_losses || 0)}L
            </div>
          </div>
        </div>
      )}

      {hasTarget && (
        <div data-testid="sc-funded-payout-bar">
          <div className="flex items-center justify-between text-[11px] mb-1">
            <span className="text-gray-400">Path to payout</span>
            <span className={`font-mono font-bold ${hitTarget ? 'text-emerald-300' : 'text-amber-300'}`}>
              ${Math.max(0, livePnl).toFixed(2)} / ${target.toFixed(2)}
              <span className="text-gray-500 ml-1">({progressPct.toFixed(0)}%)</span>
            </span>
          </div>
          <div className="h-3 rounded-full bg-zinc-800 overflow-hidden border border-zinc-700">
            <div
              className={`h-full transition-all duration-500 ${hitTarget ? 'bg-gradient-to-r from-emerald-400 to-emerald-500' : 'bg-gradient-to-r from-amber-400 via-yellow-400 to-emerald-400'}`}
              style={{ width: `${progressPct}%` }}
            ></div>
          </div>
          {!hitTarget && livePnl >= 0 && (
            <p className="text-[10px] text-amber-200/70 mt-1">
              💪 ${remaining.toFixed(2)} to next payout. Stay disciplined.
            </p>
          )}
          {livePnl < 0 && (
            <p className="text-[10px] text-rose-300 mt-1">
              ⚠️ Session is red. Climb back to flat before chasing the target.
            </p>
          )}
        </div>
      )}

      {hasDD && (
        <div data-testid="sc-funded-dd-bar">
          <div className="flex items-center justify-between text-[11px] mb-1">
            <span className={ddCritical ? 'text-rose-300 font-bold' : 'text-gray-400'}>
              Drawdown used
            </span>
            <span className={`font-mono font-bold ${ddCritical ? 'text-rose-300' : 'text-gray-300'}`}>
              ${drawdown.toFixed(2)} / ${maxDD.toFixed(2)}
              <span className="text-gray-500 ml-1">({ddUsedPct.toFixed(0)}%)</span>
            </span>
          </div>
          <div className="h-2.5 rounded-full bg-zinc-800 overflow-hidden border border-zinc-700">
            <div
              className={`h-full transition-all duration-500 ${ddCritical ? 'bg-gradient-to-r from-rose-500 to-rose-600 animate-pulse' : 'bg-gradient-to-r from-emerald-500 via-amber-400 to-rose-500'}`}
              style={{ width: `${ddUsedPct}%` }}
            ></div>
          </div>
          {ddCritical && (
            <p className="text-[10px] text-rose-300 mt-1 font-semibold">
              🛑 Only ${ddRemaining.toFixed(2)} headroom before account is at risk. STOP trading.
            </p>
          )}
          {!ddCritical && drawdown > 0 && (
            <p className="text-[10px] text-amber-200/70 mt-1">
              ${ddRemaining.toFixed(2)} cushion left.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

